"use strict";

class EC_FluxView {
    constructor({ el, ReportID }) {
        this.Container = el;
        this.ReportID = ReportID;

        this._QueryStringMap = new Map();
        this._ParameterMap = new Map();
    }

    SetQueryString(name, value) {
        this._QueryStringMap.set(name, value);
    }

    SetParameter(name, value) {
        this._ParameterMap.set(name, value);
    }

    SetPinnedPeriod(selectAs, value) {
        this.SetParameter(selectAs, value);
    }

    SetPinnedFilter(selectAs, value) {
        this.SetParameter(selectAs, value);
    }

    SetPinnedVariable(selectAs, value) {
        this.SetParameter(selectAs, value);
    }

    _GetRequest() {
        const myRequest = new ECP.EC_Request("FusionView");
        myRequest.SetReturnType(ECP.EC_ReturnType.Text);
        myRequest.AddRequestVariable("ReportID", this.ReportID);

        this._QueryStringMap.forEach((value, key) => {
            myRequest.AddRequestVariable(key, value);
        });

        this._ParameterMap.forEach((value, key) => {
            myRequest.AddParameter(key, value);
        });

        return myRequest;
    }

    GetResult() {
        return this._GetRequest().Submit();
    }

    GetHtml() {
        this.SetQueryString("Format", "HTML");
        this.SetQueryString("LoadData", "True");

        return this._GetRequest().Submit();
    }

    GetJson() {
        this.SetQueryString("Format", "JSON");
        return this._GetRequest().SetReturnType(ECP.EC_ReturnType.Json).Submit();
    }

    static FillToElements({ el, ReportID }) {
        return new Promise((resolve, reject) => {
            const myFluxView = new EC_FluxView({ el, ReportID });

            myFluxView.GetHtml().then((reportHtml) => {
                myFluxView.Container.innerHTML = reportHtml;

                resolve();
            }).catch(reject);
        });
    }
}

class FluxDisplay {
    constructor({ el, ReportID, BaseReportID }) {
        this.Container = el;
        this.ReportID = ReportID;
        this.BaseReportID = BaseReportID;
        this.ParametersStr = null;
        this.Guid = EC_Fmt.Guid();
        this.DrillDownFilterCallBack = null;
        this.IsFusionView = true;

        if (el) {
            el.setAttribute("guid", this.Guid);
            this.table = el.querySelector("table.FusionTable") || el;
            this.pinnedFiltersForm = el.querySelector(".ecp-fusion-pinned-override") || el;
            this.postToDashboardForm = el.querySelector(".ecp-fusion-post-dashboard") || el;
        }
    }

    Init() {
        const me = this;

        me.FixHeader();
        me.InitCharts();
        me.InitOrder();
        me.InitDrillDown();
        me.InitPostToDashboard();
    }

    SetDrillDownFilterCallBack(callback) {
        this.DrillDownFilterCallBack = callback;
    }

    FixHeader() {
        if (this.table) {
            const me = this;
            let headerHeight = -2;
            const headerRows = me.table.querySelectorAll("thead tr:not([rowtype=ReportHeader]");
            for (let i = 0; i < headerRows.length; i++) {
                const headerCells = headerRows[i].querySelectorAll("th");
                for (let j = 0; j < headerCells.length; j++) {
                    const th = headerCells[j];
                    // Task 957070: Allow user to freeze columns in Fusion so they always display on the left side while scrolling
                    if (th.hasAttribute("sort-desc")) {
                        th.innerHTML = `<div><span>${th.innerHTML}<i class=ews-icon-caretdown style='margin-left:5px;'></i></span></div>`;
                    } else if (th.hasAttribute("sort-asc")) {
                        th.innerHTML = `<div><span>${th.innerHTML}<i class=ews-icon-up style='margin-left:5px;'></i></span></div>`;
                    }
                    th.classList.add("sticky");
                    th.style.top = `${headerHeight < 0 ? 0 : headerHeight}px`;

                    const offsetHeight = th.offsetHeight;
                    th.style.maxWidth = `${th.offsetWidth}px`;
                    th.style.minHeight = `${offsetHeight}px`;
                    // Task 998009: Fusion Report Header Cutting Off Column Names
                    th.style.height = "100%";
                    th.style.border = "0px";

                    if (th.querySelector("input[type=checkbox]")) {
                        th.style.width = "30px";
                    } else {
                        th.style.width = `${th.offsetWidth}px`;
                        th.style.overflow = "hidden";
                    }
                }
                headerHeight += headerRows[i].offsetHeight;
            }

            const resizeReportHeader = () => {
                headerHeight = -2;
                for (let i = 0; i < headerRows.length; i++) {
                    headerHeight += headerRows[i].offsetHeight;
                }

                headerHeight = Math.max(0, headerHeight);

                const styleName = `stickyHeaderStyle_${me.ReportID}`;

                let stickyHeaderStyle = me.Container.querySelector(`#${styleName}`);
                if (!stickyHeaderStyle) {
                    stickyHeaderStyle = document.createElement("style");
                    stickyHeaderStyle.id = styleName;
                    me.Container.appendChild(stickyHeaderStyle);
                }

                stickyHeaderStyle.innerHTML = `[guid='${me.Guid}'] .FusionTable .parentHeader{box-sizing:border-box} [guid='${me.Guid}'] .FusionTable .childHeader{box-sizing:border-box}`;

                let maxParentHeight = 0;
                const parentRows = me.table.querySelectorAll("tr[rowtype=Parent]");
                for (let i = 0; i < parentRows.length; i++) {
                    const parentCells = parentRows[i].querySelectorAll("td");
                    for (let j = 0; j < parentCells.length; j++) {
                        parentCells[j].classList.add("sticky", "parentHeader");
                    }
                    maxParentHeight = Math.max(parentRows[i].offsetHeight, maxParentHeight);
                }

                let maxChildHeight = 0;
                const childRows = me.table.querySelectorAll("tr[rowtype=Child]");
                for (let i = 0; i < childRows.length; i++) {
                    const childCells = childRows[i].querySelectorAll("td");
                    for (let j = 0; j < childCells.length; j++) {
                        childCells[j].classList.add("sticky", "childHeader");
                    }
                    maxChildHeight = Math.max(childRows[i].offsetHeight, maxChildHeight);
                }

                stickyHeaderStyle.innerHTML = `[guid='${me.Guid}'] .FusionTable .parentHeader{top:${headerHeight}px; height:${maxParentHeight}px; box-sizing:border-box} [guid='${me.Guid}'] .FusionTable .childHeader{top:${headerHeight + maxParentHeight}px; height:${maxChildHeight}px; box-sizing:border-box}`;
            };

            resizeReportHeader();

            // Task 699556: Fusion: Parent header collapses and hides text when scrolling
            let resizeReportHeaderTimer;
            window.addEventListener("resize", () => {
                clearTimeout(resizeReportHeaderTimer);
                resizeReportHeaderTimer = setTimeout(() => {
                    resizeReportHeader();
                }, 100);
            });

            // Task 753638: Allow users to sync the data warehouse on demand from the fusion 'Details' tab
            // Task 1012737: Move the 'Sync Now$' button in Fusion Edit to HTML.cs so that other developers can use it
            ECP.Tools.EventBus.On("DataWarehouseSynced", async ({ _feature, tables }) => {
                const SyncTable = tables;
                const SyncTableArr = tables.split("^");
                const target = document.querySelector(`.SyncedButton[SyncTable='${SyncTable}']`);
                let targetArr = document.querySelectorAll(".SyncedButton[SyncTable]");
                if (SyncTableArr.length > 1 || targetArr.length === 1) {
                    if (document.getElementById("TabCacheAge")) {
                        EC_Fmt.TriggerEvent(document.getElementById("TabCacheAge"), "click");
                    }
                    return;
                }
                if (target) {
                    const GetTableSyncInfoAPI = new ECP.EC_Request("FusionView_GetTableSyncInfo");
                    GetTableSyncInfoAPI.SetReturnType(ECP.EC_ReturnType.Json);
                    GetTableSyncInfoAPI.AddRequestVariable("TableName", SyncTable, ECP.EC_Verb.Get);
                    // Task 1049949: Auto clear the Report Cache if Sync Now is clicked via the ManualDatawarehouseSync permission.
                    GetTableSyncInfoAPI.AddRequestVariable("ReportID", EC_Fmt.GetPageParameter("ReportID"), ECP.EC_Verb.Get);
                    const TableSyncInfo = await GetTableSyncInfoAPI.Submit();
                    document.querySelector(`.${SyncTable}State`).innerHTML = TableSyncInfo.SyncState;
                    document.querySelector(`.${SyncTable}LastSyncTime`).innerHTML = TableSyncInfo.LastSyncTime;
                    document.querySelector(`.${SyncTable}NextSyncTime`).innerHTML = TableSyncInfo.NextSyncTime;
                    if (TableSyncInfo.SyncState !== "Pending") {
                        target.remove();
                        targetArr = document.querySelectorAll(".SyncedButton[SyncTable]");
                    }
                    if (targetArr.length === 2) {
                        for (let i = 0; i < targetArr.length; i++) {
                            const Targethtml = targetArr[i];
                            if (EC_Fmt.Null2EmptyStr(Targethtml.getAttribute("SyncTable")).includes("^")) {
                                Targethtml.remove();
                            }
                        }
                    }
                }
            });
        }
    }

    InitCharts() {
        const me = this;
        const charts = me.Container.querySelectorAll("svg.ews-chart");
        for (let i = 0; i < charts.length; i++) {
            const chartElement = charts[i];
            const graph = chartElement.closest(".NoHover");
            // Task 1161510: Javascript error 'Cannot read properties of null (reading 'getBoundingClientRect')' when drilling down on a fusion report on a dashboard
            const ReportContent = document.getElementById("ReportContent");
            if (graph && ReportContent) {
                // Task 1124295: Default width of a graph on FusionView should be the width of the screen
                const width = ReportContent.getBoundingClientRect().width - 12 - 15; // 12px = padding on the graph, 15px = size of scroll bar
                let panelWidth = document.body.offsetWidth / 5;
                if (panelWidth < 345) {
                    panelWidth = 345;
                }
                graph.style.width = `${width - (EC_Fmt.isMobile ? 0 : panelWidth)}px`;
            }
            EC_FluxGraph.initChart(chartElement);
        }
        const NoHover = me.Container.querySelector(".NoHover");
        if (NoHover) {
            const chartResize = EC_Fmt.HtmlStrToElement("<div class='chartResize ews-icon-emcaretdown'></div>");
            NoHover.appendChild(chartResize);
            chartResize.addEventListener("mousedown", (event) => { ChartMousedown(event, NoHover); });
            NoHover.addEventListener("mouseenter", (e) => {
                if (e.target && e.target.querySelector("svg.ews-chart")) {
                    chartResize.style.display = "unset";
                }
            });
            NoHover.addEventListener("mouseleave", () => {
                chartResize.style.display = "none";
            });
        }

        function ChartMousedown(event, chart) {
            event.stopPropagation();
            event.preventDefault();

            const marginWidth = event.x - chart.offsetWidth;
            const marginHeight = event.y - chart.offsetHeight;
            let mousedownX;
            let mousedownY;
            const mousemoveHandle = (mouseEvent) => {
                mousedownX = mouseEvent.x - marginWidth;
                chart.style.width = `${mousedownX}px`;
                mousedownY = mouseEvent.y - marginHeight;
                chart.style.height = `${mousedownY}px`;
            };

            const mouseupHandle = () => {
                document.removeEventListener("mousemove", mousemoveHandle);
                document.removeEventListener("mouseup", mouseupHandle);
                chart.style.width = `${mousedownX}px`;
                chart.style.height = `${mousedownY}px`;
                const chartElement = chart.querySelector(".draft-ecp-fusion-graph svg") || chart.querySelector(".ecp-fusion-graph svg");
                chartElement.innerHTML = "";
                EC_FluxGraph.initChart(chartElement);
            };
            document.addEventListener("mousemove", mousemoveHandle);
            document.addEventListener("mouseup", mouseupHandle);
        }
    }

    InitOrder(onAfterSort) {
        const me = this;

        if (me.table) {
            const orderContainer = me.table.querySelectorAll("[rowtype=ColumnHeader] th");
            for (let i = 0; i < orderContainer.length; i++) {
                if (EC_Fmt.isMobile) {
                    orderContainer[i].addEventListener("click", OrderEvent);
                } else {
                    orderContainer[i].addEventListener("click", OrderEvent);
                }
            }
        }

        function OrderEvent(e) {
            if (!e.target.classList.contains("ews-icon-insertsheet")) {
                me.OrderReport(e.currentTarget, onAfterSort);
            }
        }
    }

    OrderReport(trigger, onAfterSort) {
        // Task 740331: Fusion View: Can not change Sort Order by clicking on Column Header
        const me = this;
        const field = trigger.getAttribute("field");
        const dataTypeStr = trigger.getAttribute("dataType");
        const elementtd = trigger.closest("th");
        const ColumnIndex = Array.from(elementtd.parentElement.children).indexOf(elementtd); // get Column Index
        const goals = trigger.getAttribute("fusiongoals") + trigger.getAttribute("fusionofgoals");
        // Task 1132965: Fusion in Dashboard is not removed after sorting
        const HasSort = (trigger.hasAttribute("sort-desc") || trigger.hasAttribute("sort-asc")) ? "HasSort" : ""; // need Dashboard Fusion
        const FieldName = EC_Fmt.CssEscape(`OrderMenu_${me.ReportID}_${field}_${ColumnIndex}_${goals}_${HasSort}`);
        let OrderMenu = document.getElementById(FieldName);

        const AlreadyOpen = (OrderMenu && OrderMenu?.style.display !== "none");

        HideAllDivs();
        AllowHideAllDivs = false;
        const SortState = !trigger.matches("[sort]");
        if (!OrderMenu) {
            const IsOrder = trigger.hasAttribute("order-trigger");
            OrderMenu = document.createElement("div");
            OrderMenu.id = `${FieldName}`;
            OrderMenu.classList.add("ColumnMenu", "OrderMenu", "Hideable");
            let OrderMenuHtml = (IsOrder ? `${GetMenuItemHtml(EC_Fmt.DataTypeParse(dataTypeStr), SortState)}<div class=cut-off></div>` : "");
            OrderMenuHtml += GetMenuFreezeItemHtml();

            const ReportAuthentication = document.getElementById("report-OwnerAuthenticationID");
            const GoalsTypeArr = ["_Currency", "_Integer", "_Decimal"];
            // Task 1116609: Freeze Pane on Dashboard with Multiple Fusion Reports
            if (!trigger.classList.contains("FusionOfGoals")
                && (field.toLowerCase().indexOf("value") > -1 || field.toLowerCase().indexOf("Column") > -1)
                && GoalsTypeArr.includes(dataTypeStr)
                && ReportAuthentication
                && ReportAuthentication.value === AuthenticationID.toString()) {
                OrderMenuHtml += GetMenuGoalsItemHtml(trigger);
            }
            OrderMenu.innerHTML = OrderMenuHtml;
            document.body.appendChild(OrderMenu);
            if (IsOrder) {
                OrderMenu.querySelector(".FusionSortAsc").addEventListener("click", () => {
                    ExecuteSort(me.ReportID, me.BaseReportID, "Asc", onAfterSort);
                });
                OrderMenu.querySelector(".FusionSortDesc").addEventListener("click", () => {
                    ExecuteSort(me.ReportID, me.BaseReportID, "Desc", onAfterSort);
                });
            }
            // Task 742642: Fusion Edit: Design Order By Add new Field type for Order By Fields
            if (SortState && IsOrder) {
                OrderMenu.querySelector(".FusionSortRemove").addEventListener("click", () => {
                    ExecuteSort(me.ReportID, me.BaseReportID, "", onAfterSort);
                });
            }
            const FusionUnfreezeColumn = OrderMenu.querySelector(".FusionUnfreezeColumn") || OrderMenu.querySelector(".FusionFreezeColumn");
            const CurrentFusionTable = trigger.closest(".FusionTable");
            if (FusionUnfreezeColumn) {
                FusionUnfreezeColumn.addEventListener("click", () => {
                    ExecuteFloatColumn(FusionUnfreezeColumn, CurrentFusionTable);
                });
            }
            // Task 1091677: Fusion Goals: Allowing users to type-in and save goals for each row on a fusion report
            const FusionGoals = OrderMenu.querySelector("[FusionGoals=add]");
            if (FusionGoals) {
                FusionGoals.addEventListener("click", ExecuteGoals);
            }
            const HigherLower = OrderMenu.querySelectorAll("[FusionOfGoals]");
            for (let i = 0; i < HigherLower.length; i++) {
                HigherLower[i].addEventListener("click", HigherLowerclick);
            }
            const GoalsSaveDom = OrderMenu.querySelector("[FusionGoals=save]");
            if (GoalsSaveDom) {
                GoalsSaveDom.addEventListener("click", GoalsSave);
            }
            const GoalsEditDom = OrderMenu.querySelector("[FusionGoals=edit]");
            if (GoalsEditDom) {
                GoalsEditDom.addEventListener("click", GoalsEdit);
            }
            const GoalsRemoveDom = OrderMenu.querySelector("[FusionGoals=remove]");
            if (GoalsRemoveDom) {
                GoalsRemoveDom.addEventListener("click", GoalsRemove);
            }
        }

        if (!AlreadyOpen) {
            FluxDisplay.ShowPopMenu(trigger, OrderMenu);
        }

        function GetMenuItemHtml(dataType, ExistingSortState) {
            let htmlB = "";
            if (EC_Fmt.IsNumericDataType(dataType)) {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionSortAsc"><i class=ews-icon-sortasc></i><label class=sort-label>Sort 1 ➔ 9</label></div>`
                    + `<div class="ColumnMenuItem OrderMenuItem FusionSortDesc"><i class=ews-icon-sortdesc></i><label class=sort-label>Sort 9 ➔ 1</label></div>`;
            } else if (EC_Fmt.IsDateDataType(dataType)) {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionSortAsc"><i class=ews-icon-sortasc></i><label class=sort-label>Sort ${EC_Fmt.InputFmt("2010-01-01", ECP.DataType._MonthDay, 12, false)} ➔ ${EC_Fmt.InputFmt("2000-12-31", ECP.DataType._MonthDay, 12, false)}</label></div>`
                    + `<div class="ColumnMenuItem OrderMenuItem FusionSortDesc"><i class=ews-icon-sortdesc></i><label class=sort-label>Sort ${EC_Fmt.InputFmt("2000-12-31", ECP.DataType._MonthDay, 12, false)} ➔ ${EC_Fmt.InputFmt("2010-01-01", ECP.DataType._MonthDay, 12, false)}</label></div>`;
            } else {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionSortAsc"><i class=ews-icon-sortasc></i><label class=sort-label>Sort A ➔ Z</label></div>`
                    + `<div class="ColumnMenuItem OrderMenuItem FusionSortDesc"><i class=ews-icon-sortdesc></i><label class=sort-label>Sort Z ➔ A</label></div>`;
            }
            if (ExistingSortState) {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionSortRemove"><label class=sort-label>Remove sorting</label></div>`;
            }
            return htmlB;
        }

        function GetMenuFreezeItemHtml(FreezeState) {
            let htmlB = "";
            if (FreezeState) {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionUnfreezeColumn"columnindex=${ColumnIndex} style=cursor:pointer><label class=freezename style=margin-left:5px>Unfreeze column</label></div >`;
            } else {
                htmlB += `<div class="ColumnMenuItem OrderMenuItem FusionFreezeColumn"columnindex=${ColumnIndex} style=cursor:pointer><label class=freezename style=margin-left:5px>Freeze column</label></div >`;
            }
            return htmlB;
        }

        // Task 957070: OCloud Fusion: similar to floating headers, allow frozen columns
        function ExecuteFloatColumn(el, FusionTable) {
            const ColumnI = EC_Fmt.Null2ZeroInt(el.getAttribute("columnindex"));
            const RowArr = FusionTable.querySelectorAll("tr [rowtype=ColumnHeader],tr [rowtype=Detail],tr [rowtype=Parent],tr [rowtype=Child]");
            // Task 1116609: Freeze Pane on Dashboard with Multiple Fusion Reports
            const ReportID = EC_Fmt.Null2ZeroInt(FusionTable.getAttribute("reportid"));

            if (el.classList.contains("FusionUnfreezeColumn")) {
                el.classList.remove("FusionUnfreezeColumn");
                el.classList.add("FusionFreezeColumn");
                el.innerHTML = "<label class=freezename style=margin-left:5px>Freeze column</label>";
                const RemoveRowArr = FusionTable.querySelectorAll(`th.stickyleft[freezecolumn='${ColumnI}'], td.stickyleft[freezecolumn='${ColumnI}']`);
                for (let i = 0; i < RemoveRowArr.length; i++) {
                    RemoveRowArr[i].classList.remove("stickyleft");
                }
            } else {
                el.classList.remove("FusionFreezeColumn");
                el.classList.add("FusionUnfreezeColumn");
                el.innerHTML = "<label class=freezename style=margin-left:5px>Unfreeze column</label>";

                for (let i = 0; i < RowArr.length; i++) {
                    const rowType = RowArr[i].getAttribute("rowtype");
                    if (["ColumnHeader", "Parent", "Child"].includes(rowType)) {
                        RowArr[i].classList.add("FreezeColumn");
                    }
                    const RowCell = RowArr[i].querySelectorAll("td,th");
                    RowCell[ColumnIndex].classList.add("stickyleft");
                    RowCell[ColumnIndex].classList.add(`stickyleft${ColumnIndex}`);
                    RowCell[ColumnIndex].setAttribute("freezecolumn", ColumnIndex);
                    // Task 1083137: Scrolling down on a fusion report with a frozen column causes the header on the frozen column to be covered on iOS Safari
                    if (EC_Fmt.Browser === ECP.Browser.Safari && rowType === "ColumnHeader") {
                        RowCell[ColumnIndex].style.zIndex = 3;
                    }
                }
            }
            if (RowArr[0]) {
                const stickyleftArr = RowArr[0].querySelectorAll(".stickyleft");
                let style = "";
                let setWidth = 0;
                for (let i = 0; i < stickyleftArr.length; i++) {
                    style += `#Tab0 .FusionTable .stickyleft${stickyleftArr[i].getAttribute("freezecolumn")}{left:${setWidth}px}`;
                    setWidth += parseFloat(getComputedStyle(stickyleftArr[i]).width.replace("px", ""));
                }
                const FusionFreezeColumnStyle = document.getElementById(`FusionFreezeColumnStyle${ReportID}`);
                if (FusionFreezeColumnStyle) {
                    FusionFreezeColumnStyle.remove();
                }
                const printStyleEle = document.createElement("STYLE");
                printStyleEle.setAttribute("type", "text/css");
                printStyleEle.id = `FusionFreezeColumnStyle${ReportID}`;
                printStyleEle.innerHTML = style;
                FusionTable.appendChild(printStyleEle);
            }
        }

        async function ExecuteSort(reportId, baseReportID, direction, e) {
            direction = EC_Fmt.isNull(direction) ? "" : `${field}.${direction}`;
            // Task 1126762: Sorting on a drill-down report does not sort it
            let data = { ReportID: reportId, OrderBy: direction };
            if (!e) {
                ECP.Dialog.ShowLoading();
                const myRequest = new ECP.EC_Request("FluxEdit_OrderReport");
                myRequest.AddRequestVariable("ReportID", reportId, ECP.EC_Verb.Post);
                if (!EC_Fmt.isNull(me.BaseReportID)) {
                    myRequest.AddRequestVariable("BaseReportID", baseReportID, ECP.EC_Verb.Post);
                }
                myRequest.AddRequestVariable("OrderBy", direction, ECP.EC_Verb.Post);
                data = await myRequest.Submit();
            }
            let redirect = true;
            if (e) {
                redirect = e(data);
                // Task 911769: Fusion Drill Down popup window sort column does not work
                const NewOrderMenu = document.getElementById(EC_Fmt.CssEscape(`OrderMenu_${reportId}_${field}`));
                if (NewOrderMenu) {
                    NewOrderMenu.remove();
                }
            }
            if (redirect) {
                if (EC_Fmt.Null2ZeroInt(data.BaseReportID) > 0) {
                    window.location.href = `Home?DashboardID=${ECP.CommonDashboards.FusionView}&ReportID=${data.ReportID}&BaseReportID=${data.BaseReportID}&OrderBy=${direction}&${EC_Fmt.Get_QueryString("DashboardID;ReportID;BaseReportID;OrderBy")}`;
                } else {
                    window.location.href = `Home?DashboardID=${ECP.CommonDashboards.FusionView}&ReportID=${data.ReportID}&OrderBy=${direction}&${EC_Fmt.Get_QueryString("DashboardID;ReportID;BaseReportID;OrderBy")}`;
                }
            }
        }

        // Task 1091677: Fusion Goals: Allowing users to type-in and save goals for each row on a fusion report
        function GetMenuGoalsItemHtml(element) {
            const Field = element.getAttribute("field");
            const IsFusionGoals = element.getAttribute("FusionGoals");
            const IsFusionOfGoals = element.getAttribute("FusionOfGoals");
            let html = `<div class="ColumnMenuItem OrderMenuItem" FusionGoals=add columnindex=${ColumnIndex} field=${Field} style=cursor:pointer><i class=ews-icon-trophyempty></i><label style=margin-left:5px>Add Goals</label></div >`;
            if (IsFusionGoals) {
                if (IsFusionGoals.toLowerCase() === "edit") {
                    html = `<div class="ColumnMenuItem OrderMenuItem" FusionGoals=edit columnindex=${ColumnIndex} field=${Field} style=cursor:pointer><i class=ews-icon-trophyempty></i><label style=margin-left:5px>Edit Goals</label></div >`;
                } else if (IsFusionGoals.toLowerCase() === "save") {
                    html = "";
                    if (IsFusionOfGoals?.toLowerCase() === "lower") {
                        html += `<div class='ColumnMenuItem OrderMenuItem' FusionOfGoals=higher field=${field}><i class=ews-icon-sortasc></i><label class=sort-label>Higher</label></div>`;
                    } else {
                        html += `<div class='ColumnMenuItem OrderMenuItem' FusionOfGoals=lower field=${field}><i class=ews-icon-sortdesc></i><label class=sort-label>Lower</label></div>`;
                    }
                    html += `<div class='ColumnMenuItem OrderMenuItem' FusionGoals=save columnindex=${ColumnIndex} field=${field}><i class=ews-icon-save></i><label class=sort-label>Save Goals</label></div>`;
                } else if (IsFusionGoals.toLowerCase() === "remove") {
                    html = `<div class="ColumnMenuItem OrderMenuItem" FusionGoals=remove columnindex=${ColumnIndex} field=${Field} style=cursor:pointer><i class=ews-icon-trophyempty></i><label style=margin-left:5px>Remove Goals</label></div >`;
                }
            }
            return html;
        }

        function ExecuteGoals() {
            const el = this;
            const ColumnI = EC_Fmt.Null2ZeroInt(el.getAttribute("columnindex"));
            const Field = el.getAttribute("field");
            const FusionTable = document.querySelector("#Tab0 .FusionTable");
            if (FusionTable?.querySelector(`th.FusionGoals[field=${Field}]`)) {
                return;
            }
            const thFusionGoals = FusionTable.querySelector(`th[field=${Field}]`);
            const datatype = thFusionGoals.getAttribute("datatype") || "_Integer";
            thFusionGoals.setAttribute("FusionGoals", "remove");
            const RowArr = FusionTable.querySelectorAll("tr [rowtype=ColumnHeader],tr [rowtype=Detail],tr [rowtype=Parent],tr [rowtype=Child]");
            for (let i = 0; i < RowArr.length; i++) {
                const rowType = RowArr[i].getAttribute("rowtype") === "ColumnHeader";

                const RowCell = RowArr[i].querySelectorAll("td,th")[ColumnI];
                const NewGoals = RowCell.cloneNode();
                NewGoals.setAttribute("field", Field);
                NewGoals.removeAttribute("order-trigger");
                NewGoals.removeAttribute("sort");
                const NewofGoals = NewGoals.cloneNode();
                NewGoals.setAttribute("FusionGoals", "");
                if (rowType) {
                    NewGoals.setAttribute("FusionGoals", "save");
                    NewGoals.setAttribute("FusionOfGoals", "higher");

                    NewGoals.classList.add("FusionGoals");
                    NewGoals.innerText = `(${RowCell.innerText}) Goals`;
                    NewGoals.title = `(${RowCell.title}) Goals`;
                    NewGoals.addEventListener("click", (e) => { me.OrderReport(e.currentTarget); });

                    NewofGoals.removeAttribute("fusiongoals");
                    NewofGoals.classList.add("FusionOfGoals");
                    NewofGoals.innerText = `(${RowCell.innerText}) % of Goals`;
                    NewofGoals.title = `(${RowCell.title}) % of Goals`;
                    NewofGoals.addEventListener("click", (e) => { me.OrderReport(e.currentTarget); });
                } else {
                    let goalsrowmd5 = RowArr[i].getAttribute("goalsrowmd5");
                    if (!goalsrowmd5) {
                        let rowsting = RowArr[i].getAttribute("parent");
                        rowsting += RowArr[i].hasAttribute("child") ? RowArr[i].getAttribute("child") : "";
                        rowsting += RowArr[i].hasAttribute("detail") ? RowArr[i].getAttribute("detail") : "";
                        goalsrowmd5 = `_${EC_Fmt.MD5(rowsting.replaceAll("\t", ""))}`;
                    }
                    const GoalsInput = EC_Fmt.HtmlStrToElement(`<input field=${Field} goalsrowmd5=${goalsrowmd5} onchange=EC_Fmt.InputFmt(this,'${datatype}',18,false,true) placeholder=Goals>`); // type=number
                    GoalsInput.addEventListener("change", OfGoalsCompute);
                    NewGoals.append(GoalsInput);
                    NewofGoals.setAttribute("FusionOfGoals", "higher");
                    NewofGoals.classList.add("OfGoalsLower");
                    NewofGoals.innerHTML = "0%";
                }
                RowCell.parentElement.insertBefore(NewGoals, RowCell.nextSibling);
                NewGoals.parentElement.insertBefore(NewofGoals, NewGoals.nextSibling);
            }
        }
        function OfGoalsCompute() {
            const Field = this.getAttribute("field");
            const tr = this.closest("tr");
            const OfGoals = tr.querySelector(`[FusionOfGoals][field=${Field}]`);
            const GoalsHL = OfGoals.getAttribute("fusionofgoals");
            const originalItem = tr.querySelector(`span[original][field=${Field}]`);
            const GoalsValue = EC_Fmt.CDec(this.value);
            const originalGoalsValue = EC_Fmt.CDec(originalItem.innerText);
            let OfGoalsValue = 0;
            if (OfGoals && originalGoalsValue !== 0 & GoalsValue !== 0) {
                OfGoalsValue = EC_Fmt.CDec((originalGoalsValue / GoalsValue * 100).toFixed(1));
            }
            if ((OfGoalsValue >= 100 && GoalsHL === "higher") || (OfGoalsValue > 100 && GoalsHL === "lower")) {
                OfGoals.classList.remove("OfGoalsLower");
                OfGoals.classList.add("OfGoalsHigher");
            } else {
                OfGoals.classList.remove("OfGoalsHigher");
                OfGoals.classList.add("OfGoalsLower");
            }
            OfGoals.innerHTML = `${OfGoalsValue}%`;
        }

        function HigherLowerclick() {
            const clickField = this.getAttribute("field");
            const FusionTable = document.getElementById("Tab0");
            const HigherLower = this.getAttribute("FusionOfGoals").toLowerCase();
            const LowerArr = FusionTable.querySelectorAll(`td[field=${clickField}][FusionOfGoals], th[field=${clickField}][FusionOfGoals] `);
            for (let i = 0; i < LowerArr.length; i++) {
                if (EC_Fmt.Null2ZeroInt(LowerArr[i].innerText) !== 100) {
                    LowerArr[i].setAttribute("FusionOfGoals", HigherLower);
                }
            }
        }
        function GoalsEdit() {
            const el = this;
            const Field = el.getAttribute("field");
            const FusionTable = document.getElementById("Tab0");
            const GoalsArr = FusionTable.querySelectorAll(`td[fusiongoals] span[field=${field}]`);
            const fusiongoals = FusionTable.querySelector(`th[fusiongoals=edit][field=${field}]`);
            const datatype = fusiongoals.getAttribute("datatype") || "_Integer";

            fusiongoals.setAttribute("fusiongoals", "save");
            for (let i = 0; i < GoalsArr.length; i++) {
                const Item = GoalsArr[i];
                const Value = EC_Fmt.isNull(Item.innerText) ? "" : EC_Fmt.Null2ZeroInt(Item.innerText);
                const GoalsInput = EC_Fmt.HtmlStrToElement(`<input field=${Field} oldvalue='${Value} ' goalsid=${Item.getAttribute("goalsid")} value='${Value}' goalsrowmd5=${Item.getAttribute("goalsrowmd5")} onchange=EC_Fmt.InputFmt(this,'${datatype}',18,false,true) placeholder=Goals>`);
                Item.parentElement.append(GoalsInput);
                Item.remove();
                GoalsInput.addEventListener("change", OfGoalsCompute);
            }
        }

        function GoalsRemove() {
            const thisme = this;
            const goalsField = thisme.getAttribute("field");
            const FusionTable = document.getElementById("Tab0");
            const FusionGoalsArr = FusionTable.querySelectorAll(`td[FusionGoals] span[field=${goalsField}], td[FusionGoals] input[field=${goalsField}]`);
            const FusionGoalsDic = [];
            for (let i = 0; i < FusionGoalsArr.length; i++) {
                const FusionGoalsItem = FusionGoalsArr[i];
                if (EC_Fmt.Null2ZeroInt(FusionGoalsItem.getAttribute("goalsid")) > 0) {
                    const GoalsDic = {
                        GoalsID: FusionGoalsItem.getAttribute("goalsid"),
                        Goal: ""
                    };
                    FusionGoalsDic.push(GoalsDic);
                }
            }
            const myRequest = new ECP.EC_Request("FluxExtended_SaveGoals");
            myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Get);
            myRequest.AddRequestVariable("FusionGoals", JSON.stringify(FusionGoalsDic), ECP.EC_Verb.Post);
            myRequest.Submit().then(() => {
                const RemoveArr = FusionTable.querySelectorAll(`td[field=${goalsField}][fusiongoals],td[field=${field}][fusionofgoals],th.FusionGoals[field=${goalsField}],th.FusionOfGoals[field=${goalsField}`);
                for (let i = 0; i < RemoveArr.length; i++) {
                    RemoveArr[i].remove();
                }
                FusionTable.querySelector(`th[field=${goalsField}][fusiongoals=remove]`).setAttribute("fusiongoals", "add");
                LoadFusionGraph();
                ECP.Dialog.Alert("Delete Goals success.");
            });
        }
        function GoalsSave() {
            const thisme = this;
            const goalsField = thisme.getAttribute("field");
            const FusionTable = document.getElementById("Tab0");
            const HigherLower = FusionTable.querySelector(`th[FusionOfGoals][field=${goalsField}]`).getAttribute("FusionOfGoals");
            let FusionGoalsArr = FusionTable.querySelectorAll(`td[FusionGoals] input[field=${goalsField}]`);
            const FusionGoalsDic = [];
            for (let i = 0; i < FusionGoalsArr.length; i++) {
                const FusionGoalsItem = FusionGoalsArr[i];
                const value = EC_Fmt.CDec(FusionGoalsItem.value);
                const oldvalue = EC_Fmt.CDec(FusionGoalsItem.getAttribute("oldvalue"));
                if ((!EC_Fmt.isNull(FusionGoalsItem.value) && value > 0 && value !== oldvalue) || EC_Fmt.Null2ZeroInt(FusionGoalsItem.getAttribute("goalsid")) > 0) {
                    const GoalsDic = {
                        GoalsID: FusionGoalsItem.getAttribute("goalsid"),
                        ValueColumn: FusionGoalsItem.getAttribute("field"),
                        RowIdentifier: FusionGoalsItem.getAttribute("goalsrowmd5"),
                        Goal: value,
                        HigherLower
                    };
                    FusionGoalsDic.push(GoalsDic);
                }
            }
            const myRequest = new ECP.EC_Request("FluxExtended_SaveGoals");
            myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Get);
            myRequest.AddRequestVariable("FusionGoals", JSON.stringify(FusionGoalsDic), ECP.EC_Verb.Post);
            myRequest.Submit().then((json) => {
                if (!EC_Fmt.isNull(json)) {
                    for (let i = 0; i < json.length; i++) {
                        const Item = json[i];
                        const IsValidGoal = EC_Fmt.CDec(Item.Goal) !== 0;
                        const GoalsSpan = EC_Fmt.HtmlStrToElement(`<span field=${field} ${IsValidGoal ? `goalsid=${Item.GoalsID}` : ""} goalsrowmd5=${Item.RowIdentifier}>${Item.Goal}</span>`);
                        const FusionGoals = FusionTable.querySelector(`td[FusionGoals] input[field=${field}][goalsrowmd5=${Item.RowIdentifier}`);
                        FusionGoals.parentElement.append(GoalsSpan);
                        FusionGoals.remove();
                    }
                }
                FusionGoalsArr = FusionTable.querySelectorAll(`td[FusionGoals] input[field=${field}]`);
                for (let i = 0; i < FusionGoalsArr.length; i++) {
                    const FusionGoalsItem = FusionGoalsArr[i];
                    const GoalsSpan = EC_Fmt.HtmlStrToElement(`<span field=${field} goalsid=${FusionGoalsItem.getAttribute("goalsid")} goalsrowmd5=${FusionGoalsItem.getAttribute("goalsrowmd5")}>${FusionGoalsItem.value}</span>`);
                    FusionGoalsItem.parentElement.append(GoalsSpan);
                    FusionGoalsItem.remove();
                }
                const FusionGoalsTh = FusionTable.querySelector(`th[FusionGoals=save][field=${field}]`);
                FusionGoalsTh.setAttribute("fusiongoals", "edit");

                LoadFusionGraph();
            });
        }

        function LoadFusionGraph() {
            let chart = me.Container.querySelector("svg.ews-chart");
            if (!chart) {
                return;
            }
            chart = chart.closest(".NoHover");

            const chartElement = chart.querySelector(".draft-ecp-fusion-graph svg") || chart.querySelector(".ecp-fusion-graph svg");
            chartElement.innerHTML = "";
            EC_FluxGraph.initChart(chartElement);
        }
    }

    InitCollapsing(URLHash, Expand, callback) {
        // EC-12470
        const DataHtml = this.Container?.querySelector(".FusionTable") || this.Container;
        if (!DataHtml || DataHtml.querySelectorAll(`tr[rowtype=Parent],tr[rowtype=Child]`).length === 0) {
            return;
        }
        const ColumnHeader = DataHtml.querySelector("tr[rowtype=ColumnHeader] th");
        const ParentArr = DataHtml.querySelectorAll("tr[rowtype=Parent]");
        const ChildArr = DataHtml.querySelectorAll("tr[rowtype=Child]");

        let ExpandedArr = [];
        if (!EC_Fmt.isNull(URLHash)) {
            ExpandedArr = EC_Fmt.GetURLParameters(URLHash).Expanded || "";
            ExpandedArr = ExpandedArr.split(",");
            for (let i = 0; ExpandedArr.length > i; i++) {
                if (i > 0 && !ExpandedArr[i].startsWith("Parent")) {
                    ExpandedArr[i - 1] = `${ExpandedArr[i - 1]},${ExpandedArr[i]}`;
                    ExpandedArr.splice(i, 1);
                    i--;
                }
            }
        } else if (Expand && !EC_Fmt.isNull("Expand")) {
            switch (Expand.toString()) {
                case "0": {
                    break;
                }
                case "2": {
                    ExpandedArr.push("all");
                    break;
                }
                default: {
                    if (ChildArr.length > 0) {
                        const FirstChild = `${ChildArr[0].getAttribute("parent")}|${ChildArr[0].getAttribute("child")}`;
                        ExpandedArr.push(FirstChild);
                    }
                    if (ParentArr.length > 0) {
                        const FirstParent = `${ParentArr[0].getAttribute("parent")}`;
                        ExpandedArr.push(FirstParent);
                    }
                    break;
                }
            }
        }

        const IconHtml = EC_Fmt.HtmlStrToElement("<div class=ews-icon-insertsheet></div>"); //  onclick='Collapsing(this);event.stopPropagation();'
        let IsColumnRotateicon = false;
        if (ChildArr.length > 0) {
            for (let i = 0; ChildArr.length > i; i++) {
                const Child = ChildArr[i];
                const IconHtmlNode = IconHtml.cloneNode();
                if (!ExpandedArr.includes(Child.getAttribute("parent")) && !ExpandedArr.includes("all")) {
                    Child.classList.add("CollapseHidden");
                } else if (ExpandedArr.includes(`${Child.getAttribute("parent")}|${Child.getAttribute("child")}`) || ExpandedArr.includes("all")) {
                    IconHtmlNode.classList.add("rotateicon");
                    IsColumnRotateicon = true;
                }
                const Childtd = Child.querySelector("td");
                Childtd.insertBefore(IconHtmlNode, Childtd.firstChild);
            }
        }
        if (ParentArr.length > 0) {
            for (let i = 0; ParentArr.length > i; i++) {
                const Parent = ParentArr[i];
                const ParentTd = ParentArr[i].querySelector("td");
                const IconHtmlNode = IconHtml.cloneNode();
                if (ExpandedArr.includes(Parent.getAttribute("parent")) || ExpandedArr.includes("all")) {
                    IconHtmlNode.classList.add("rotateicon");
                    IsColumnRotateicon = true;
                }
                ParentTd.insertBefore(IconHtmlNode, ParentTd.firstChild);
            }
        }
        if (ParentArr.length > 0 || ChildArr.length > 0) {
            const DetailArr = DataHtml.querySelectorAll("tr[rowtype=Detail]");
            for (let i = 0; DetailArr.length > i; i++) {
                const Detail = DetailArr[i];
                const ExpanStr = ChildArr.length > 0 ? `${Detail.getAttribute("parent")}|${Detail.getAttribute("child")}` : Detail.getAttribute("parent");
                if (!ExpandedArr.includes(ExpanStr) && !ExpandedArr.includes("all")) {
                    Detail.classList.add("CollapseHidden");
                } else {
                    IsColumnRotateicon = true;
                }
            }
        }
        if (ColumnHeader) {
            const span = `<span>${ColumnHeader.innerHTML}</span>`;
            ColumnHeader.innerHTML = span;
            const IconHtmlNode = IconHtml.cloneNode();
            if (IsColumnRotateicon) {
                IconHtmlNode.classList.add("rotateicon");
            }
            ColumnHeader.insertBefore(IconHtmlNode, ColumnHeader.firstChild);
            ColumnHeader.style.whiteSpace = "nowrap";
        }
        DataHtml.addEventListener("click", (e) => {
            if (e.target.tagName === "DIV" && e.target.classList.contains("ews-icon-insertsheet")) {
                FluxDisplay.Collapsing(e.target, this.Container, callback);
                e.stopPropagation();
            }
        });
    }

    static Collapsing(e, Container, callback) {
        const FusionTable = Container;
        if (!FusionTable) {
            return;
        }
        let updatedAnchor = "";
        const tr = e.closest("tr");
        const IsCollapse = !e.classList.contains("rotateicon");
        if (IsCollapse) {
            e.classList.add("rotateicon");
        } else {
            e.classList.remove("rotateicon");
        }
        const rowtype = tr.getAttribute("rowtype");
        if (rowtype === "ColumnHeader") {
            const AllArr = FusionTable.querySelectorAll(`tr[rowtype=Parent],tr[rowtype=Child],tr[rowtype=Detail]`);
            for (let i = 0; i < AllArr.length; i++) {
                const CurTr = AllArr[i];
                const CurRowType = CurTr.getAttribute("rowtype");
                if (CurRowType === "Parent" || CurRowType === "Child") {
                    if (!IsCollapse) {
                        AllArr[i].querySelector("div").classList.remove("rotateicon");
                    } else {
                        AllArr[i].querySelector("div").classList.add("rotateicon");
                    }
                }
                if (CurRowType === "Child" || CurRowType === "Detail") {
                    if (IsCollapse) {
                        CurTr.classList.remove("CollapseHidden");
                    } else {
                        CurTr.classList.add("CollapseHidden");
                    }
                }
            }
            if (IsCollapse) {
                updatedAnchor = "all";
            } else {
                updatedAnchor = "";
            }
        } else {
            const parent = tr.getAttribute("parent");
            const ChildArrAll = FusionTable.querySelectorAll(`tr[rowtype=Child][parent]`);
            const ChildArr = Array.from(ChildArrAll).filter(child => child.getAttribute("parent") === parent);

            const child = tr.getAttribute("child");
            let DetailArr;
            if (rowtype === "Child") {
                const DetailAll = Array.from(FusionTable.querySelectorAll(`tr[rowtype=Detail][parent][child]`));
                DetailArr = DetailAll.filter(detail => detail.getAttribute("parent") === parent && detail.getAttribute("child") === child);
            } else {
                const DetailAll = Array.from(FusionTable.querySelectorAll(`tr[rowtype=Detail][parent]`));
                DetailArr = DetailAll.filter(detail => detail.getAttribute("parent") === parent);
            }

            if (rowtype === "Parent" && ChildArr.length > 0) {
                for (let i = 0; i < ChildArr.length; i++) {
                    if (IsCollapse) {
                        ChildArr[i].classList.remove("CollapseHidden");
                    } else {
                        ChildArr[i].classList.add("CollapseHidden");
                    }
                    if (!IsCollapse) {
                        ChildArr[i].querySelector("div").classList.remove("rotateicon");
                    }
                }
            }
            if (rowtype === "Child" || ChildArr.length === 0 || !IsCollapse) {
                for (let i = 0; i < DetailArr.length; i++) {
                    if (IsCollapse) {
                        DetailArr[i].classList.remove("CollapseHidden");
                    } else {
                        DetailArr[i].classList.add("CollapseHidden");
                    }
                }
            }
            const ColumnHeader = FusionTable.querySelector("tr[rowtype=ColumnHeader] div.ews-icon-insertsheet");
            if (document.querySelectorAll(".FusionTable tbody div.rotateicon").length > 0) {
                ColumnHeader.classList.add("rotateicon");
            } else {
                ColumnHeader.classList.remove("rotateicon");
            }
            // check expanded
            const ExpandedArr = FusionTable.querySelector("tbody").querySelectorAll("tbody td .rotateicon");
            for (let i = 0; i < ExpandedArr.length; i++) {
                const CurExpanded = ExpandedArr[i].closest("tr[rowtype]");
                let ExpandedStr = encodeURIComponent(CurExpanded.getAttribute("parent"));
                const CurRowType = CurExpanded.getAttribute("rowtype");
                if (CurRowType?.toLowerCase() === "child") {
                    ExpandedStr += `|${encodeURIComponent(CurExpanded.getAttribute("child"))}`;
                }
                updatedAnchor += updatedAnchor.length > 0 ? `,${ExpandedStr}` : ExpandedStr;
            }
        }
        if (callback) {
            callback(`Expanded=${updatedAnchor}`);
        }
    }

    InitDrillDown() {
        if (this.table) {
            // Task 937777: Dashboard filters will repeat the loading event when creating a Report.
            const me = this.table.querySelector(".FusionTable") || this.table;
            if (me) {
                me.addEventListener("click", (e) => {
                    const target = e.target;

                    if (target.tagName === "SPAN") {
                        const CurrentTr = target.closest("tr[RowType]");

                        let parent = null;
                        let child = null;
                        let detail = null;
                        let columnValue = null;
                        const FusionFieldType = CurrentTr?.getAttribute("FusionFieldType");
                        if (CurrentTr && !FusionFieldType) {
                            parent = CurrentTr.getAttribute("parent");
                            child = CurrentTr.getAttribute("child");
                            detail = CurrentTr.getAttribute("detail");
                            columnValue = CurrentTr.getAttribute("columnvalue");
                        } else if (FusionFieldType) {
                            // Task 1004474: Add option to switch rows and columns on the fusion report data table
                            const CurrentTd = target.closest("td");
                            const TrArr = document.querySelectorAll("#Tab0 tr[fusionfieldtype=Parent]");
                            parent = "";
                            for (let i = 0; i < TrArr.length; i++) {
                                const CurTr = TrArr[i];
                                if (i > 0) {
                                    parent += "\t";
                                }
                                parent += `${CurTr.getAttribute("parent")}^${CurTr.querySelectorAll("td")[CurrentTd.cellIndex].innerText}`;
                            }
                        }
                        if (parent || child || detail || columnValue) {
                            e.cancelBubble = true;
                            e.stopPropagation();
                            // Task 655842: Enable fusion drill down through fusion graphs
                            this.openDrillDown(parent, child, detail, columnValue, target);
                        }
                    }
                });
            }
        }
    }

    openDrillDown(parent, child, detail, columnValue, target) {
        // Task 655842: Enable fusion drill down through fusion graphs
        const me = this;
        // Task 663094: Fusion Drill down JavaScript erro
        const CurrentTr = target.closest("tr[RowType]");
        const IsChart = target.closest("svg");
        (new Promise((resolve, reject) => {
            // Task 1114376: Fusion Report Graph(cards) in dashboard is not reflecting the right numbers when doing drilldown
            const ParametersStr = EC_Fmt.GetPageParameter("Parameters");
            const DrilDownCache = "IsChart";
            let DrillDownFields = document.getElementById(`DrillDownFields_${me.ReportID}_${IsChart ? DrilDownCache : ""}`);

            EWS.VerifySession(async () => {
                if (!DrillDownFields) {
                    const myRequest = new ECP.EC_Request("GetDrillDownMenuOptions");
                    myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Get);
                    // Task 1024422: Fusion variable quick edit change not respected for the value header name
                    if (EC_Fmt.Null2EmptyStr(ParametersStr).length > 4000) {
                        myRequest.AddRequestVariable("Parameters", ParametersStr, ECP.EC_Verb.Post);
                    } else {
                        myRequest.AddRequestVariable("Parameters", ParametersStr, ECP.EC_Verb.Get);
                    }
                    const json = await myRequest.Submit();
                    const fields = [];

                    json.Data.sort((a, b) => {
                        let v = 0;
                        if (a.Category < b.Category) {
                            v = -1;
                        } else if (a.Category === b.Category) {
                            if (a.Display < b.Display) {
                                v = -1;
                            } else if (a.Display === b.Display) {
                                v = 0;
                            } else if (a.Display > b.Display) {
                                v = 1;
                            }
                        } else if (a.Category > b.Category) {
                            v = 1;
                        }
                        return v;
                    });

                    let prevCategory = "";
                    for (let i = 0; i < json.Data.length; i++) {
                        const item = json.Data[i];
                        if (prevCategory !== item.Category) {
                            fields.push({
                                class: "CategoryTitle",
                                name: item.Category
                            });
                            prevCategory = item.Category;
                        }

                        fields.push({
                            class: `Item Category${item.FusionFieldType === "PinValue" ? " PinValueCategory" : ""}`,
                            name: item.Display,
                            value: item.Value,
                            datatype: item.DataType
                        });
                    }

                    let template;
                    let addCommentDiv = "";

                    if (!IsChart) {
                        addCommentDiv = `<ecp-list type=clickable><ecp-list-item class="Item Category"id=ShowAddComment><span class="box-more-icon ews-icon-pluslight"></span><ecp-list-item-title class=box-more-text>Add Comment</ecp-list-item-title></ecp-list-item></ecp-list>`;
                    }
                    if (!EC_Fmt.isMobile || IsChart) {
                        template = `<div class="DrillDownMenu Hideable"id="DrillDownFields_${me.ReportID}_${IsChart ? DrilDownCache : ""}">`
                            + `${addCommentDiv}`
                            + `<ecp-list type=clickable class=loadFilter field="${target.getAttribute("field")}"fieldvalue="${target.innerText}">`
                            + `<ecp-list-subheader>Filters</ecp-list-subheader>`
                            + `<ecp-divider></ecp-divider>`
                            + `<ecp-list-item class=Include><span class=ews-icon-filter></span><ecp-list-item-title>Include</ecp-list-item-title></ecp-list-item>`
                            + `<ecp-list-item class=Exclude><span class=ews-icon-filter></span><ecp-list-item-title>Exclude</ecp-list-item-title></ecp-list-item>`
                            + `</ecp-list>`
                            + `<ecp-list class=DrillDownList type=clickable>`
                            + `<ecp-list-subheader>Drill Downs</ecp-list-subheader>`
                            + `<ecp-divider></ecp-divider>`;
                        for (let i = 0; i < fields.length; i++) {
                            const item = fields[i];
                            if (item.class === "CategoryTitle") {
                                template += `<ecp-list-subheader class=CategoryTitle>${item.name}</ecp-list-subheader>`;
                            } else {
                                template += `<ecp-list-item class="${item.class}"FusionFieldSelectAs="${item.value}"datatype="${item.datatype}">${item.name}</ecp-list-item>`;
                            }
                        }
                        template += `</ecp-list>`
                            + `</div>`;
                    } else {
                        template = `<tr class="DrillDownMenuMobile Hideable"id="DrillDownFields_${me.ReportID}_${IsChart ? DrilDownCache : ""}">`
                            + `<td colspan=100%>${addCommentDiv}`
                            + `<ecp-list type=clickable class=loadFilter field="${target.getAttribute("field")}"fieldvalue="${target.innerText}">`
                            + `<ecp-list-subheader>Filters</ecp-list-subheader>`
                            + `<ecp-divider></ecp-divider>`
                            + `<ecp-list-item class=Include><span class=ews-icon-filter></span><ecp-list-item-title>Include</ecp-list-item-title></ecp-list-item>`
                            + `<ecp-list-item class=Exclude><span class=ews-icon-filter></span><ecp-list-item-title>Exclude</ecp-list-item-title></ecp-list-item>`
                            + `</ecp-list>`
                            + `<ecp-list type=clickable class=DrillDownList>`
                            + `<ecp-list-subheader>Drill Downs</ecp-list-subheader>`
                            + `<ecp-divider></ecp-divider>`;
                        for (let i = 0; i < fields.length; i++) {
                            const item = fields[i];
                            if (item.class === "CategoryTitle") {
                                template += `<ecp-list-subheader class=CategoryTitle>${item.name}</ecp-list-subheader>`;
                            } else {
                                template += `<ecp-list-item class="${item.class}"FusionFieldSelectAs="${item.value}"datatype="${item.datatype}">${item.name}</ecp-list-item>`;
                            }
                        }
                        template += `</ecp-list>`
                            + `</td>`
                            + `</tr>`;
                    }
                    DrillDownFields = EC_Fmt.HtmlStrToElement(template);
                }

                const filterTarget = target.closest("td") || target;
                let loadFilter = false;
                const regex = /^(Value|Parent|Child|Detail)/;
                if (filterTarget && regex.test(filterTarget.getAttribute("field")) && filterTarget.parentElement.getAttribute("rowtype") !== "GrandTotal") {
                    loadFilter = true;
                }
                if (!loadFilter) {
                    DrillDownFields.querySelector(".loadFilter").style.display = "none";
                } else {
                    DrillDownFields.querySelector(".loadFilter").style.display = "";
                }

                function drillDownFunc(aEvent) {
                    const value = DrillDownFields.getAttribute("curValue");
                    const title = `${value.length > 12 ? `${value.substring(0, 9)}...` : value} - ${aEvent.target.DrillDownText || aEvent.target.innerText}`;
                    let drillDownColumnValue = "";
                    const field = DrillDownFields.getAttribute("field");
                    if (field.startsWith("Column")) {
                        const headerValue = me.Container.querySelector(`tr[rowtype=ColumnHeader] th[field=${DrillDownFields.getAttribute("field")}]`);
                        if (headerValue) {
                            drillDownColumnValue = headerValue.getAttribute("headervalue");
                        }
                    }
                    me.CreateDrillDown(title, aEvent.target.getAttribute("FusionFieldSelectAs"), DrillDownFields.getAttribute("parent"), DrillDownFields.getAttribute("child"), DrillDownFields.getAttribute("detail"), drillDownColumnValue, me.DrillDownDialogID);
                }
                const anchorsWithoutHref = DrillDownFields.querySelectorAll("ecp-list.DrillDownList ecp-list-item:not(.PinValueCategory)");
                for (let i = 0; i < anchorsWithoutHref.length; i++) {
                    const a = EC_Fmt.RemoveAllEventListeners(anchorsWithoutHref[i]);
                    a.addEventListener("click", drillDownFunc);
                }
                const addCommentsBtn = DrillDownFields.querySelectorAll("#ShowAddComment");
                for (let i = 0; i < addCommentsBtn.length; i++) {
                    const a = EC_Fmt.RemoveAllEventListeners(addCommentsBtn[i]);
                    a.addEventListener("click", async () => {
                        await AddComments(target);
                    });
                }
                const includeExcludeBtn = DrillDownFields.querySelectorAll("ecp-list-item.Include, ecp-list-item.Exclude");
                for (let i = 0; i < includeExcludeBtn.length; i++) {
                    const a = EC_Fmt.RemoveAllEventListeners(includeExcludeBtn[i]);
                    a.addEventListener("click", IncludeExcludeFilter);
                }
                function IncludeExcludeFilter(e) {
                    const curent = e.currentTarget;
                    if (curent.tagName !== "ECP-LIST-ITEM") {
                        return;
                    }
                    const isInclude = curent.classList.contains("Include");
                    const field = curent.closest("ecp-list").getAttribute("field");
                    const fieldValue = curent.closest("ecp-list").getAttribute("fieldvalue");
                    const CustomFilter = `F:${field}~V:${fieldValue}~O:${isInclude ? "E" : "NE"}`;
                    let result = {
                        CustomFilter,
                        redirect: true
                    };
                    if (me.DrillDownFilterCallBack) {
                        result = me.DrillDownFilterCallBack(result);
                    }
                    if (result.redirect) {
                        const URLCustomFilter = EC_Fmt.GetPageParameter("CustomFilter");
                        if (URLCustomFilter) {
                            result.CustomFilter += `|${URLCustomFilter}`;
                        }
                        result.CustomFilter = mergeCustomFilter(result.CustomFilter);
                        window.location.href = `Home?${EC_Fmt.Get_QueryString("CustomFilter")}&CustomFilter=${result.CustomFilter}`;
                    }
                }
                async function AddComments(cellTarget) {
                    const commentParent = DrillDownFields.getAttribute("parent");
                    const commentChild = DrillDownFields.getAttribute("child");
                    const commentDetail = DrillDownFields.getAttribute("detail");
                    const commentField = cellTarget.getAttribute("field");
                    const cellPosition = cellTarget.closest("td").getAttribute("cellposition");
                    let commentFlag = `${commentParent}|`;
                    if (!EC_Fmt.isNull(commentChild)) {
                        commentFlag += `${commentChild}|`;
                    }
                    if (!EC_Fmt.isNull(commentDetail)) {
                        commentFlag += `${commentDetail}|`;
                    }
                    commentFlag += commentField;

                    if (EC_Fmt.isMobile && me.IsFusionView) {
                        document.querySelector(".PhoneQuickEditButton").click();
                    }
                    me.RemoveActiveComment(cellPosition);
                    if (!cellPosition) {
                        cellTarget.closest("td").setAttribute("cellposition", commentFlag);
                        cellTarget.closest("td").setAttribute("active", "");
                        await me.CommentsAction("Add", { commentFlag, cellDiv: cellTarget.closest("td") });
                    } else {
                        await me.CommentItemActiveToggle(document.querySelector(`.comment-item[cellposition="${cellPosition}"]`));
                        if (me.IsFusionView) {
                            document.getElementById("CommentsIcon").setAttribute("selected", "selected");
                            await ToggleQuickcontent("Comments");
                        }
                    }
                }
                resolve(DrillDownFields);
            }, false, null, (obj) => {
                obj.message = "Session Expired, Login to enable drill down...";
                reject(obj);
            });
        })).then((DrillDownFields) => {
            // Task 675423: Transaction Detail Report isn't showing
            DrillDownFields.querySelectorAll(".InsertPinValue").remove();

            DrillDownFields.setAttribute("curValue", target.DrillDownText || target.innerText);
            DrillDownFields.setAttribute("parent", parent);
            DrillDownFields.setAttribute("child", child);
            DrillDownFields.setAttribute("detail", detail);
            DrillDownFields.setAttribute("field", target.getAttribute("field"));

            const list = EC_Fmt.isNull(columnValue) ? "" : columnValue.split("\t");

            for (let i = 0; i < list.length; i++) {
                const tmpArr = list[i].split("^");
                for (let j = i + 1; j < list.length; j++) {
                    const tmpArr2 = list[j].split("^");
                    if (tmpArr[1] === tmpArr2[1]) {
                        list.splice(j, 1);
                    }
                }
            }
            const HyperlinkMD5List = [];
            for (let i = 0; i < list.length; i++) {
                let tmpArr2 = "";
                const tmpArr = list[i].split("^");
                const FusionFieldSelectAs = tmpArr[0].substring(tmpArr[0].indexOf("PinValue"), tmpArr[0].length);
                if (tmpArr.length > 1) {
                    tmpArr2 = tmpArr[1].replace("&#94;", "^").split("^");
                }
                const Display = tmpArr2[0];

                if (tmpArr2.length > 1) {
                    const Link = tmpArr2[1].replace(/&#94;/g, "^");
                    const pinValue = DrillDownFields.querySelector(`ecp-list-item.PinValueCategory[fusionfieldselectas=${FusionFieldSelectAs}]`);
                    if (pinValue) {
                        // Task 1028629: Fusion View Drill Down: Additional Values
                        const FusionFieldSelectAlias = pinValue.getAttribute("FusionFieldSelectAs");
                        const IsRepeat = pinValue.getAttribute("datatype") === "_Hyperlink" ? HyperlinkMD5List.includes(EC_Fmt.MD5(FusionFieldSelectAlias + Display + Link)) : false;
                        if (!IsRepeat) {
                            pinValue.parentNode.insertBefore((EC_Fmt.HtmlStrToElement(`<a class="Item Category InsertPinValue" FusionFieldSelectAs="${FusionFieldSelectAlias}" href="${Link}">${Display}</a>`)), pinValue.nextSibling);
                            HyperlinkMD5List.push(EC_Fmt.MD5(FusionFieldSelectAs + Display + Link));
                        }
                    }
                }
            }

            if (!EC_Fmt.isMobile) {
                document.body.append(DrillDownFields);
                HideAllDivs();
                FluxDisplay.ShowPopMenu(target, DrillDownFields);
            } else {
                if (CurrentTr) {
                    CurrentTr.parentNode.insertBefore(DrillDownFields, CurrentTr.nextSibling);
                } else {
                    document.body.append(DrillDownFields);
                    // Task 765585: Fusion Report Graph Javascript Error when clicking on graph elements in ios safari
                    if (IsChart) {
                        HideAllDivs();
                        FluxDisplay.ShowPopMenu(target, DrillDownFields);
                    }
                }
                DrillDownFields.style.display = "table-row";
            }
        }).catch((obj) => {
            ECP.Dialog.HideLoading();
            ECP.Dialog.Alert(obj);
        });
    }

    CreateDrillDown(title, selectAs, parent, child, detail, columnValue, drillDownDialogID) {
        const me = this;

        let dialog = drillDownDialogID ? document.getElementById(drillDownDialogID) : null;
        let dialogContainer = null;
        let dialogContent = null;
        let expandDrillDownBtn = null;

        if (dialog) {
            dialogContent = dialog.querySelector(".ecp-dialog-content");
            expandDrillDownBtn = dialog.querySelector(".ecp-drilldown-expand > ecp-btn");
            dialogContainer = dialog.querySelector(".ecp-dialog-container");
        } else {
            let offsetLeft = 0;
            if (!EC_Fmt.isMobile) {
                const dialogList = document.querySelectorAll("div[id^=ecp-dialog-container]");
                if (dialogList.length > 0) {
                    offsetLeft = dialogList[dialogList.length - 1].offsetLeft + 20;
                }
            }

            dialog = ECP.Dialog.ShowDialog("", "", Object.assign(EC_Fmt.isMobile ? {
                width: window.innerWidth > 0 ? window.innerWidth : window.screen.width,
                height: window.innerHeight > 0 ? window.innerHeight : window.screen.height
            } : {}, {
                disableModal: !EC_Fmt.isMobile,
                disableButtons: true,
                confirmButtonAction() { },
                cancelButtonAction(myDialog) {
                    const fusionTables = myDialog.querySelectorAll(".FusionTable");
                    for (let i = 0; i < fusionTables.length; i++) {
                        if (!EC_Fmt.isNull(EWS.TableCalculators) && !EC_Fmt.isNull(EWS.TableCalculators.Fusion)) {
                            TableCalculator.RemoveTable(EWS.TableCalculators.Fusion, fusionTables[i]);
                        }
                    }
                }
            }));

            dialog.id = EC_Fmt.Guid();
            // Task 1041296: Fusion View drill down style is incorrect
            // Task 1042026: Fusion View Drill down with large number of rows extends off screen. I can not see top or bottom, can not scroll
            dialogContainer = dialog.querySelector(".ecp-dialog-container");
            dialogContainer.firstChild.classList.add("ecp-drilldown-title");
            dialogContainer.querySelector(".ecp-dialog-footer").style.display = "none";
            // Task 1137404: Fusion View drilldown Style Error
            dialogContainer.append(EC_Fmt.HtmlStrToElement("<div class=ecp-drilldown-expand><ecp-btn href=javascript:void(0) type=ghost icon=ews-icon-break-out></ecp-btn></div>"));

            dialogContent = dialog.querySelector(".ecp-dialog-content");
            dialogContent.style.padding = "2px 5px 5px";
            dialogContent.innerHTML = "<fieldset class=ecp-drilldown-fieldset style=box-sizing:border-box><legend class=drill-down-title> </legend><div class=drill-down-content style=margin-top:10px> </div></fieldset>";

            expandDrillDownBtn = dialog.querySelector(".ecp-drilldown-expand > ecp-btn");

            dialog.addEventListener("mousedown", () => {
                const dialogContainers = document.querySelectorAll("div[id^=ecp-dialog-container]");
                for (let i = 0; i < dialogContainers.length; i++) {
                    dialogContainers[i].style.zIndex = "288";
                }
                dialog.firstChild.style.zIndex = "300";
            });

            if (offsetLeft > 0) {
                dialog.firstChild.style.left = `${offsetLeft}px`;
            }
        }

        // Task 673663: Fusion Drill-Down 'Loading' is not in the specified position
        EWS.ShowLoading(0, dialogContainer);

        const myRequest = new ECP.EC_Request("FluxEdit_CreateDrillDownReport");
        myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Post);
        myRequest.AddRequestVariable("FusionFieldSelectAs", selectAs, ECP.EC_Verb.Post);
        myRequest.AddRequestVariable("Parent", parent, ECP.EC_Verb.Post);
        myRequest.AddRequestVariable("Child", child, ECP.EC_Verb.Post);
        myRequest.AddRequestVariable("Detail", detail, ECP.EC_Verb.Post);
        myRequest.AddRequestVariable("ColumnValue", columnValue, ECP.EC_Verb.Post);

        myRequest.Submit().then((json) => {
            if (json.Message) {
                ECP.Dialog.Alert(json.Message);
                return;
            }

            const drillDownDivs = dialogContent.querySelectorAll(".drill-down-content > div");
            const drillDownSpans = dialogContent.querySelectorAll(".drill-down-title > span");
            for (let i = 0; i < drillDownSpans.length; i++) {
                drillDownSpans[i].classList.remove("ecp-drilldown-title-active");
            }
            const titleEl = EC_Fmt.HtmlStrToElement(`<span reportId='${json.ReportID}' class=ecp-drilldown-title-active>${title}</span>`);
            titleEl.style.cursor = "pointer";

            titleEl.addEventListener("click", () => {
                // Task 643498: Cannot go back to previous breadcrumb reports in Fusion drilldown
                const NewdrillDownDivs = dialogContent.querySelectorAll(".drill-down-content > div");
                const NewdrillDownSpans = dialogContent.querySelectorAll(".drill-down-title > span");
                for (let i = 0; i < NewdrillDownSpans.length; i++) {
                    NewdrillDownSpans[i].classList.remove("ecp-drilldown-title-active");
                }
                titleEl.classList.add("ecp-drilldown-title-active");
                for (let i = 0; i < NewdrillDownDivs.length; i++) {
                    if (NewdrillDownDivs[i].getAttribute("reportId") === titleEl.getAttribute("reportId")) {
                        NewdrillDownDivs[i].style.display = "block";
                    } else {
                        NewdrillDownDivs[i].style.display = "none";
                    }
                }
                // Task 1049221: Drill down on fusion report from dashboard does not respect the filters at the top if the filters have changed from their original value
                expandDrillDownBtn.href = `Home?DashboardID=${ECP.CommonDashboards.FusionView}&ReportID=${titleEl.getAttribute("reportId")}&Parameters=${FluxDisplay.ParametersToUrl(FluxDisplay.GetParameters(me.ParametersStr))}`;
            });

            const DrillDownTitle = dialogContent.querySelector(".drill-down-title");
            if (drillDownSpans.length > 0) {
                DrillDownTitle.append(EC_Fmt.HtmlStrToElement(`<span> > </span>`));
            }
            DrillDownTitle.append(titleEl);
            // Task 841328: Dashboard Item Report Graph drill downs not respecting Omni Filters
            // Task 948393: Omni Filters are not respected when drilling down using graph points.
            let DashboardReportURL = me.table.getAttribute("src");
            const FluxViewContainer = me.table.closest(".FluxViewContainer");
            const Dahsboarddrilldown = me.table.closest(".ecp-dialog-container");
            if (!me.table.classList.contains("FluxViewContainer") && FluxViewContainer) {
                DashboardReportURL = FluxViewContainer.getAttribute("src");
            } else if (EC_Fmt.isNull(me.ParametersStr) && EC_Fmt.isNull(DashboardReportURL) && !FluxViewContainer && Dahsboarddrilldown) {
                // Task 1020780: Fusion Graph: Error when using drill-down function on fusion graph multiple times
                // The chart and data are two DrillDown events, so the events are not synchronized.
                DashboardReportURL = Dahsboarddrilldown.querySelector(".ecp-drilldown-expand ecp-btn").getAttribute("href");
            }
            if (EC_Fmt.isNull(me.ParametersStr) && !EC_Fmt.isNull(DashboardReportURL)) {
                me.ParametersStr = EC_Fmt.Null2EmptyStr(EC_Fmt.GetURLParameters(DashboardReportURL).Parameters);
            }
            expandDrillDownBtn.href = `Home?DashboardID=${ECP.CommonDashboards.FusionView}&ReportID=${json.ReportID}&Parameters=${FluxDisplay.ParametersToUrl(FluxDisplay.GetParameters(me.ParametersStr))}`;

            const el = EC_Fmt.HtmlStrToElement(`<div reportId="${json.ReportID}"> </div>`);
            for (let i = 0; i < drillDownDivs.length; i++) {
                drillDownDivs[i].style.display = "none";
            }
            dialogContent.querySelector(".drill-down-content").append(el);

            FillInDialog(el, json.ReportID, "").then(() => {
                EWS.HideLoading();

                // Task 704230: Drill down on a report in a dashboard takes up the full screen when viewing on iPadOS Safari and the user cannot move the popup around
                if (dialogContainer) {
                    const maxWidth = window.innerWidth > 0 ? window.innerWidth : window.screen.width;
                    const maxHeight = window.innerHeight > 0 ? window.innerHeight : window.screen.height;
                    dialogContainer.style.top = `${maxHeight / 2 - dialogContainer.clientHeight / 2}px`;
                    dialogContainer.style.left = `${maxWidth / 2 - dialogContainer.clientWidth / 2}px`;
                }
            });
        });

        function FillInDialog(el, ReportID, OrderBy) {
            return new Promise((resolve) => {
                const myDrillDownFluxView = new EC_FluxView({
                    ReportID
                });

                const parameters = FluxDisplay.GetParameters(me.ParametersStr);
                const keys = Object.keys(parameters);
                for (let i = 0; i < keys.length; i++) {
                    myDrillDownFluxView.SetParameter(keys[i], parameters[keys[i]]);
                }
                if (!EC_Fmt.isNull(OrderBy)) {
                    myDrillDownFluxView.SetQueryString("OrderBy", OrderBy);
                }
                myDrillDownFluxView.GetHtml().then((html) => {
                    el.innerHTML = "";
                    const dom = EC_Fmt.HtmlStrToElement(`<div>${html}</div>`);

                    // Task 837520: Drill down on Fusion Report displays the 'Details' information if the drill down report returns no results
                    const reportEl = dom.querySelector("#Tab0");
                    if (reportEl) {
                        const styleElements = dom.querySelectorAll("style");
                        for (let i = 0; i < styleElements.length; i++) {
                            el.append(styleElements[i]);
                        }
                        el.append(reportEl);

                        const myDrillDownFluxDisplay = new FluxDisplay({ el: reportEl, ReportID });
                        myDrillDownFluxDisplay.DrillDownDialogID = dialog.id;
                        // Task 947289: Omni Filter is not being applied to when drilling down multiple times.
                        if (me && !EC_Fmt.isNull(me.ParametersStr)) {
                            myDrillDownFluxDisplay.ParametersStr = me.ParametersStr;
                        }
                        myDrillDownFluxDisplay.InitOrder((data) => {
                            EWS.ShowLoading(0, el);
                            FillInDialog(el, data.ReportID, data.OrderBy).then(() => {
                                EWS.HideLoading();
                            });
                        });
                        myDrillDownFluxDisplay.FixHeader();
                        myDrillDownFluxDisplay.InitDrillDown();
                        myDrillDownFluxDisplay.InitCharts();
                        if (!EC_Fmt.isNull(EWS.TableCalculators) && !EC_Fmt.isNull(EWS.TableCalculators.Fusion)) {
                            myDrillDownFluxDisplay.InitTableCalculator();
                        }
                    } else {
                        const container = dom.querySelector("div.ecp-fusion-container");
                        if (container) {
                            el.append(container);
                        } else {
                            el.append(dom);
                        }
                    }
                    resolve();
                });
            });
        }
    }

    async InitPinnedFilter(onAfterFilter) {
        const me = this;
        const tabContainer = me.Container;
        if (tabContainer) {
            const fusionFiltersHtml = EC_Fmt.HtmlStrToElement(`<div id=FusionFilters style=background-color:var(--bg)></div>`);
            if (tabContainer.firstChild) {
                tabContainer.insertBefore(fusionFiltersHtml, tabContainer.firstChild);
            } else {
                tabContainer.appendChild(fusionFiltersHtml);
            }

            const myRequest = new ECP.EC_Request("FluxView_GetReportPinTabs");
            myRequest.AddRequestVariable("ReportID", me.ReportID);
            const CustomFilter = EC_Fmt.GetPageParameter("CustomFilter");
            if (!EC_Fmt.isNull(CustomFilter)) {
                myRequest.AddRequestVariable("CustomFilter", CustomFilter);
            }
            let StrParameters = decodeURIComponent(EC_Fmt.Null2EmptyStr(EC_Fmt.GetPageParameter("Parameters")));
            // -- load Dashboard Filed
            const DashboardReportURL = tabContainer.getAttribute("src");
            if (!EC_Fmt.isNull(DashboardReportURL)) {
                StrParameters = EC_Fmt.Null2EmptyStr(EC_Fmt.GetURLParameters(DashboardReportURL).Parameters);
            }

            if (!EC_Fmt.isNull(StrParameters)) {
                if (StrParameters.length > 4000 || EC_Fmt.Null2EmptyStr(EC_Fmt.GetPageParameter("Parameters")).length > 4000) {
                    myRequest.AddRequestVariable("Parameters", StrParameters, ECP.EC_Verb.Post);
                } else {
                    myRequest.AddRequestVariable("Parameters", StrParameters, ECP.EC_Verb.Get);
                }
            }

            myRequest.SetReturnType(ECP.EC_ReturnType.Text);
            const ReportPinTabs = await myRequest.Submit();
            tabContainer.querySelector("#FusionFilters").appendChild(EC_Fmt.HtmlStrToElement(`<div class=FiltersContent>${ReportPinTabs}</div>`));
            const FiltersRequired = tabContainer.querySelector(".Filtersrequired");
            let FiltersRequiredShowHTML = "";
            if (FiltersRequired?.innerHTML) {
                FiltersRequiredShowHTML = `<div style=padding:10px;font-size:20px>${FiltersRequired.innerHTML}</div>`;
            }
            tabContainer.querySelector("#FusionFilters").appendChild(EC_Fmt.HtmlStrToElement(`<div id=FiltersrequiredShow style=text-align:center>${FiltersRequiredShowHTML}</div>`));
        }

        if (me.pinnedFiltersForm) {
            EWS.Input.Init(me.pinnedFiltersForm);

            // AjaxInput
            const ajaxInputArr = me.pinnedFiltersForm.querySelectorAll("input.Autocomplete");
            for (let i = 0; i < ajaxInputArr.length; i++) {
                if (ajaxInputArr[i].closest("table").nextSibling) {
                    // Task 1137364: For Fusion View Filter select length over 4000.
                    new ECP.HTML.AjaxInput(ajaxInputArr[i], "", "", (_, hidden) => {
                        const filedValue = hidden.value.length;
                        if (filedValue > 4000) {
                            ECP.HTML.Snackbar(`The values in '${hidden.name}' filter exceed 4000 characters, additional values will not be added. Please filter on a field with a smaller stored value or use multiple variables.`, "Warning");
                        }
                    });
                }
            }

            const fusionPeriods = me.pinnedFiltersForm.querySelectorAll(".fusion-pinned-period");
            for (let i = 0; i < fusionPeriods.length; i++) {
                const fusionPeriod = fusionPeriods[i];
                const key = fusionPeriod.getAttribute("key");
                const periodType = fusionPeriod.getAttribute("period-type");
                const periodInput = me.pinnedFiltersForm.querySelector(`#${key}`);
                if (periodType === "Period") {
                    const Inputs = fusionPeriod.querySelectorAll("input");
                    for (let j = 0; j < Inputs.length; j++) {
                        Inputs[j].addEventListener("blur", () => {
                            const periodYearsInput = document.querySelector(`div[key="${key}"] input[type="hidden"]`);
                            const yearsValue = document.querySelector(`div[key="${key}"] ecp-input[id="PeriodYears"]`).value;
                            periodYearsInput.value += `^${yearsValue}`;
                        });
                    }
                } else if (periodType === "DateRange") {
                    const startDate = me.pinnedFiltersForm.querySelector(`#StartDate${key}`);
                    const endDate = me.pinnedFiltersForm.querySelector(`#EndDate${key}`);
                    // Task 743043: Fusion Reports -> Quick Edit: Optimize Design
                    if (startDate) {
                        startDate.removeAttribute("name");
                    }
                    if (endDate) {
                        endDate.removeAttribute("name");
                    }

                    const dateInputs = fusionPeriod.querySelectorAll("ecp-input[type=date]");
                    for (let j = 0; j < dateInputs.length; j++) {
                        // Task 1129362: Date Range is not work in our Reports.
                        dateInputs[j].addEventListener("change", () => {
                            // Task 1125747: Fusion: Creating an invalid Date Range in the Filter from Filters Side tab doesn't allow you to fix mistake
                            const startDateValue = document.getElementById(`StartDate${key}`).value;
                            const endDateValue = document.getElementById(`EndDate${key}`).value;
                            const yearsValue = document.querySelector(`div[key="${key}"] ecp-input[id="PeriodYears"]`).value;
                            if (!EC_Fmt.isNull(startDateValue) && !EC_Fmt.isNull(endDateValue) && EC_Fmt.ToDate(startDateValue) > EC_Fmt.ToDate(endDateValue)) {
                                ECP.HTML.Snackbar(EC_Fmt.Trn("Start Date is greater than the End Date."), "Info");
                            }
                            periodInput.value = `${startDateValue}^${endDateValue}^${yearsValue}`;
                        });
                    }
                } else if (periodType === "WeekRange") {
                    const weekInputs = fusionPeriod.querySelectorAll("ecp-input[type=number]");
                    for (let j = 0; j < weekInputs.length; j++) {
                        weekInputs[j].addEventListener("change", () => {
                            // Task 987416: Pin Filter Week supports inputting one
                            const StartWeek = document.getElementById(`StartWeek${key}`).value;
                            const EndWeek = document.getElementById(`EndWeek${key}`).value;
                            const yearsValue = document.querySelector(`div[key="${key}"] ecp-input[id="PeriodYears"]`).value;
                            if (!EC_Fmt.isNull(StartWeek) && (EC_Fmt.Null2ZeroInt(StartWeek) < 1 || EC_Fmt.Null2ZeroInt(StartWeek) > 53)) {
                                ECP.HTML.Snackbar("Start Week Is Out of Range: Must be between 1 and 53.", "Info");
                                document.getElementById(`StartWeek${key}`).value = "";
                            } else if (!EC_Fmt.isNull(EndWeek) && (EC_Fmt.Null2ZeroInt(EndWeek) < 1 || EC_Fmt.Null2ZeroInt(EndWeek) > 53)) {
                                ECP.HTML.Snackbar("End Week Is Out of Range: Must be between 1 and 53.", "Info");
                                document.getElementById(`EndWeek${key}`).value = "";
                            } else {
                                periodInput.value = `${StartWeek}^${EndWeek}^${yearsValue}`;
                            }
                        });
                    }
                }
            }

            const pinnedPeriodsYears = me.pinnedFiltersForm.querySelectorAll(".pinned-period-years");
            for (let i = 0; i < pinnedPeriodsYears.length; i++) {
                const periodYears = pinnedPeriodsYears[i];
                const key = periodYears.getAttribute("key");
                const periodInputs = periodYears.querySelectorAll("ecp-input");
                const periodType = periodYears.getAttribute("period-type");
                for (let j = 0; j < periodInputs.length; j++) {
                    periodInputs[j].addEventListener("change", () => {
                        const periodInput = document.querySelector(`div[key="${key}"] input[type="hidden"]`);
                        const yearsValue = document.querySelector(`div[key="${key}"] ecp-input[id="PeriodYears"]`).value;
                        const splitValue = periodInput.value.split("^");
                        if (periodType === "Period") {
                            periodInput.value = `${splitValue[0]}^${yearsValue}`;
                        } else if (periodType === "DateRange" || periodType === "WeekRange") {
                            periodInput.value = `${splitValue[0]}^${splitValue[1]}^${yearsValue}`;
                        }
                    });
                }
            }
            // Task 953291: Fusion Pinned Filter on Date Range does not label Start and End Date Fields
            const requiredFields = me.pinnedFiltersForm.querySelectorAll(".ecp-fv_flex-item .ecp-fv_column-value[required=true] input[type=text]:not(.DatePickerInput)");
            for (let i = 0; i < requiredFields.length; i++) {
                requiredFields[i].setAttribute("placeholder", EC_Fmt.Trn("Required"));
            }

            // Submit Button
            const submitBtn = me.pinnedFiltersForm.querySelector(".ecp-fusion-pinned-override-submit > input[type=button]");
            if (submitBtn) {
                // Task 937125: Dashboard filters viewed from mobile present troublesome popup
                const SubmitBtnForm = submitBtn.form;
                if (SubmitBtnForm && EC_Fmt.isMobile) {
                    SubmitBtnForm.addEventListener("submit", (e) => {
                        e.preventDefault();
                        EC_Fmt.TriggerEvent(submitBtn, "click");
                    });
                }

                submitBtn.addEventListener("click", (e) => {
                    EWS.VerifySession(async () => {
                        ECP.Dialog.ShowLoading(null, me.Container, null);
                        const fusionPeriodArr = me.pinnedFiltersForm.querySelectorAll(".fusion-pinned-period[period-type=DateRange] input");
                        for (let i = 0; i < fusionPeriodArr.length; i++) {
                            const CurValue = fusionPeriodArr[i].value;
                            if (!EC_Fmt.isNull(CurValue) && CurValue.split("^").length === 2) {
                                const startDate = CurValue.split("^")[0];
                                const endDate = CurValue.split("^")[1];
                                if (!EC_Fmt.isNull(startDate) && !EC_Fmt.isNull(endDate) && EC_Fmt.ToDate(startDate) > EC_Fmt.ToDate(endDate)) {
                                    ECP.HTML.Snackbar(EC_Fmt.Trn("Start Date is greater than the End Date."), "Info");
                                    ECP.Dialog.HideLoading();
                                    return;
                                }
                            }
                        }
                        const data = me.GetPinnedFilterFormData();
                        let IsGetRequest = true;
                        let redirect = true;
                        let StrParameters = FluxDisplay.ParametersToUrl(data, false);
                        let result = { ReportID: me.ReportID };
                        const myRequest = new ECP.EC_Request("FluxEdit_UpdateFilters");

                        if (encodeURIComponent(StrParameters).length > 4000) {
                            myRequest.AddRequestVariable("Parameters", StrParameters, ECP.EC_Verb.Post);
                            StrParameters = "";
                            IsGetRequest = false;
                        } else {
                            myRequest.AddRequestVariable("Parameters", StrParameters, ECP.EC_Verb.Get);
                            StrParameters = FluxDisplay.ParametersToUrl(data);
                        }
                        const OwnerAuthenticationID = document.getElementById("report-OwnerAuthenticationID");
                        if (!IsGetRequest || (OwnerAuthenticationID && EC_Fmt.Null2ZeroInt(OwnerAuthenticationID.value) === AuthenticationID)) {
                            myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Post);
                            if (me.BaseReportID > 0) {
                                myRequest.AddRequestVariable("BaseReportID", me.BaseReportID, ECP.EC_Verb.Post);
                            }
                            try {
                                const PinFiltersValue = this.pinnedFiltersForm.querySelector("#PinFiltersValueArr");
                                if (PinFiltersValue) {
                                    PinFiltersValue.value = JSON.stringify(Object.assign({}, JSON.parse(PinFiltersValue.value), data));
                                }
                                result = await myRequest.Submit();
                            } catch (ex) {
                                ECP.Dialog.HideLoading();
                                throw ex;
                            }
                        }

                        if (onAfterFilter) {
                            result.Parameters = StrParameters;
                            redirect = await onAfterFilter(result, me);
                            ECP.Dialog.HideLoading();
                        }
                        if (redirect) {
                            EC_Fmt.OpenWithCtrlClickSupport(e, `Home?DashboardID=${ECP.CommonDashboards.FusionView}&ReportID=${result.ReportID}${result.BaseReportID > 0 ? `&BaseReportID=${result.BaseReportID}` : ""}&${EC_Fmt.Get_QueryString(`DashboardID;ReportID;Parameters;${result.BaseReportID ? "BaseReportID" : ""}`)}${!EC_Fmt.isNull(StrParameters) ? `&Parameters=${StrParameters}` : ""}`);
                            ECP.Dialog.HideLoading();
                        }
                    });
                });
            }

            const titles = me.pinnedFiltersForm.querySelectorAll(".ecp-fusion-pinned-override-title");
            for (let i = 0; i < titles.length; i++) {
                const title = titles[i];
                title.addEventListener("click", () => {
                    const caretDiv = title.querySelector("span.caret-down,.caret-right");
                    if (caretDiv) {
                        const filterBody = me.pinnedFiltersForm.querySelectorAll(".ecp-fusion-pinned-override-filters,.ecp-fusion-pinned-override-submit");

                        if (caretDiv.classList.contains("caret-down")) {
                            for (let j = 0; j < filterBody.length; j++) {
                                filterBody[j].style.display = "none";
                            }
                            caretDiv.classList.add("caret-right");
                            caretDiv.classList.remove("caret-down");
                        } else {
                            for (let j = 0; j < filterBody.length; j++) {
                                // Task 629461: 'Filters' display at the top of a fusion report changes to be stacked horizontally after opening and closing the filters section
                                filterBody[j].style.display = "flex";
                            }
                            caretDiv.classList.add("caret-down");
                            caretDiv.classList.remove("caret-right");
                        }
                    }
                });
            }

            const categories = me.pinnedFiltersForm.querySelectorAll(".ecp-fv_record-category");
            for (let i = 0; i < categories.length; i++) {
                const category = categories[i];
                category.addEventListener("click", () => {
                    const caretDiv = category.querySelector("span.caret-down,.caret-right");
                    const categoryName = category.getAttribute("category");

                    if (caretDiv.classList.contains("caret-down")) {
                        CollapseCategory(me.pinnedFiltersForm, category, categoryName);
                    } else {
                        ExpandCategory(me.pinnedFiltersForm, category, categoryName);
                    }
                });
            }
        }

        function CollapseCategory(container, category, categoryName) {
            if (category.querySelectorAll("span.caret-down").length === 0 || !categoryName) {
                return;
            }
            const categoryItems = container.querySelectorAll(`div.ecp-fv_flex-item[category='${categoryName}']`);
            for (let i = 0; i < categoryItems.length; i++) {
                const categoryItem = categoryItems[i];
                if (!categoryItem.classList.contains("ecp-fv_record-category")) {
                    categoryItem.style.display = "none";
                }
            }
            const caret = category.querySelector("span.caret-down");
            caret.classList.add("caret-right");
            caret.classList.remove("caret-down");
            SetReportSetting(`Fusion_Pinned_Filters_Collapsed_${categoryName}`, "True");
        }

        function ExpandCategory(container, category, categoryName) {
            if (category.querySelectorAll("span.caret-right").length === 0 || !categoryName) {
                return;
            }
            const categoryItems = container.querySelectorAll(`div.ecp-fv_flex-item[category='${categoryName}']`);
            for (let i = 0; i < categoryItems.length; i++) {
                const categoryItem = categoryItems[i];
                if (!categoryItem.classList.contains("ecp-fv_record-category")) {
                    // Task 743043: Fusion Reports -> Quick Edit: Optimize Design
                    categoryItem.style.display = "";
                }
            }
            const caret = category.querySelector("span.caret-right");
            caret.classList.add("caret-down");
            caret.classList.remove("caret-right");
            SetReportSetting(`Fusion_Pinned_Filters_Collapsed_${categoryName}`, "False");
        }

        function SetReportSetting(name, value) {
            const myRequest = new ECP.EC_Request("FluxEdit_SetReportSetting");
            myRequest.AddRequestVariable("ReportID", me.ReportID, ECP.EC_Verb.Post);
            myRequest.AddRequestVariable("Name", name, ECP.EC_Verb.Post);
            myRequest.AddRequestVariable("Value", value, ECP.EC_Verb.Post);
            myRequest.Submit();
        }
    }

    GetPinnedFilterFormData() {
        // Task 964255: Fusion View: Using Pinned filters creates a new Draft ReportID.
        let PinFiltersValueArr = {};
        const PinFiltersValue = this.pinnedFiltersForm.querySelector("#PinFiltersValueArr");
        if (PinFiltersValue && !EC_Fmt.isNull(PinFiltersValue.value)) {
            PinFiltersValueArr = JSON.parse(PinFiltersValue.value);
        }
        const result = {};

        if (this.pinnedFiltersForm) {
            const fusionInputs = this.pinnedFiltersForm.querySelectorAll("input[type=hidden],ecp-input,input.fusion-pinned-input");
            for (let i = 0; i < fusionInputs.length; i++) {
                const input = fusionInputs[i];
                if (input.name && ((!PinFiltersValueArr[input.name] && !EC_Fmt.isNull(input.value)) || (PinFiltersValueArr[input.name] && PinFiltersValueArr[input.name] !== input.value))) {
                    result[input.name] = input.value;
                }
            }
        }
        return result;
    }

    InitPostToDashboard() {
        const me = this;
        if (me.postToDashboardForm) {
            const selectAllCheckBoxes = me.postToDashboardForm.querySelectorAll(".ecp-fusion-select-all");

            if (selectAllCheckBoxes?.length > 0) {
                for (let i = 0; i < selectAllCheckBoxes.length; i++) {
                    const selectAllCB = selectAllCheckBoxes[i];
                    let index = -1;
                    const selectAs = selectAllCB.getAttribute("select-as");

                    let myParentNode = selectAllCB.parentNode;
                    while (myParentNode) {
                        if (myParentNode.tagName.toLowerCase() === "th") {
                            index = myParentNode.cellIndex;
                            break;
                        }
                        myParentNode = myParentNode.parentNode;
                    }

                    if (index >= 0) {
                        const parents = document.querySelectorAll("tr[rowtype=Parent]");
                        const children = document.querySelectorAll("tr[rowtype=Child]");

                        for (let j = 0; j < parents.length; j++) {
                            const parent = parents[j];
                            if (parent.children[index]) {
                                const detail = parent.children[index];
                                if (!detail.querySelector("input[type=checkbox]")) {
                                    detail.innerHTML = getCheckBoxHtml(detail, selectAs) + detail.innerHTML;
                                }
                            }
                        }

                        for (let j = 0; j < children.length; j++) {
                            const child = children[j];
                            if (child.children[index]) {
                                const detail = child.children[index];
                                if (!detail.querySelector("input[type=checkbox]")) {
                                    detail.innerHTML += getCheckBoxHtml(detail, selectAs);
                                }
                            }
                        }
                    }
                }

                if (me.table) {
                    const SelectAll = me.table.querySelectorAll(".ecp-fusion-select-all,.ecp-fusion-select-all-sub");
                    for (let i = 0; i < SelectAll.length; i++) {
                        SelectAll[i].addEventListener("click", (event) => {
                            selectAllCheckBox(event.currentTarget, event.currentTarget.getAttribute("select-as"), event.currentTarget.getAttribute("parent"), event.currentTarget.getAttribute("child"));
                        });
                    }
                }

                const submitBtn = me.postToDashboardForm.querySelector(".ecp-fusion-post-dashboard-submit");
                if (submitBtn) {
                    submitBtn.addEventListener("click", () => {
                        EWS.VerifySession(() => {
                            me.postToDashboardForm.submit();
                        });
                    });
                }
            }
        }

        function getCheckBoxHtml(el, selectAs) {
            let parentGroup = null;
            let childGroup = null;

            // Task 610208: Checking a parent checkbox from a Checkbox Fusion Report used in a Dashboard does not check/select children entries
            const myTr = el.closest("tr[rowtype]");
            if (myTr) {
                parentGroup = myTr.getAttribute("parent");
                childGroup = myTr.getAttribute("child");
            }

            return `<input type=checkbox style=float:right class=ecp-fusion-select-all-sub select-as="${selectAs}"parent="${parentGroup || ""}"child="${childGroup || ""}"> `;
        }

        function selectAllCheckBox(el, selectAs, parentGroup, childGroup) {
            const checked = el.checked;

            if (me.postToDashboardForm) {
                const selectAllSub = me.postToDashboardForm.querySelectorAll(`.ecp-fusion-select-all-sub`);
                for (let i = 0; i < selectAllSub.length; i++) {
                    const checkbox = selectAllSub[i];
                    const mySelectAs = checkbox.getAttribute("select-as");
                    const myParentGroup = checkbox.getAttribute("parent");
                    const myChildGroup = checkbox.getAttribute("child");

                    if (selectAs === mySelectAs && (!parentGroup || parentGroup === myParentGroup) && (!childGroup || childGroup === myChildGroup)) {
                        checkbox.checked = checked;
                    }
                }

                const checkboxes = me.postToDashboardForm.querySelectorAll(`.ecp-fusion-checkbox[select-as=${selectAs}]`);
                for (let i = 0; i < checkboxes.length; i++) {
                    const checkbox = checkboxes[i];
                    // Task 610208: Checking a parent checkbox from a Checkbox Fusion Report used in a Dashboard does not check/select children entries
                    const myTr = checkbox.closest("tr[rowtype]");
                    const myParentGroup = myTr.getAttribute("parent");
                    const myChildGroup = myTr.getAttribute("child");

                    if ((!parentGroup || parentGroup === myParentGroup) && (!childGroup || childGroup === myChildGroup)) {
                        checkbox.checked = checked;
                    }
                }
            }
        }
    }

    InitTableCalculator() {
        // Task 5867423: Make Table calculator work on fusion drill down reports
        if (this.table && this.table.classList.contains("FusionTable")) {
            TableCalculator.AddTable(EWS.TableCalculators.Fusion, this.table);
        }
    }

    static ShowPopMenu(target, menu) {
        let minHeight = null;

        menu.style.height = "0";

        menu.style.display = "block";
        menu.style.zIndex = "310";
        menu.style.position = "fixed";

        const targetPosition = target.getBoundingClientRect();
        const PositionLeft = targetPosition.left;
        let x = PositionLeft;
        let y = targetPosition.top + targetPosition.height + EC_Fmt.Null2ZeroInt(target.parentElement.style.paddingTop) + 3;

        const menuPosition = menu.getBoundingClientRect();
        const contentPosition = document.body.getBoundingClientRect();
        if (x + menuPosition.width + 3 > contentPosition.width) {
            x -= PositionLeft + menuPosition.width + 3 - contentPosition.width;
        }
        if (y + menu.scrollHeight + 3 >= contentPosition.bottom) {
            if (targetPosition.top - menu.scrollHeight - 3 > 0) {
                y = targetPosition.top - menu.scrollHeight - 3;
            } else {
                minHeight = (contentPosition.height - targetPosition.height) / 2;

                if (targetPosition.top > contentPosition.height / 2) {
                    y = targetPosition.top - minHeight - 3;
                }

                menu.style.overflow = "auto";
            }
        }

        menu.style.top = `${Number.parseInt(y)}px`;
        menu.style.left = `${Number.parseInt(x)}px`;

        requestAnimationFrame(() => {
            // -16 is .OrderMenu height
            menu.style.height = `${(minHeight || menu.scrollHeight) - 16}px`;
            const TransitionExpandSectionDone = () => {
                menu.removeEventListener("transitionend", TransitionExpandSectionDone);
            };
            menu.addEventListener("transitionend", TransitionExpandSectionDone);
        });
    }

    static GetParameters(ParametersStr) {
        if (!ParametersStr && window?.PageParameters?.Parameters) {
            const parameter = {};
            const parameterKeys = Object.keys(PageParameters.Parameters);
            for (let i = 0; i < parameterKeys.length; i++) {
                parameter[PageParameters.Parameters[parameterKeys[i]].Field] = PageParameters.Parameters[parameterKeys[i]].Value;
            }
            // Task 640292: System error when using drill down report in fusion report: Index was outside the bounds of the array.
            return parameter;
        }
        if (!ParametersStr && window?.FluxPageParameters?.Parameters) {
            return FluxPageParameters.Parameters;
        }

        const parameter = {};
        if (!ParametersStr) {
            ParametersStr = EC_Fmt.GetURLParameters(EC_Fmt.Get_QueryString("", "Parameters"));
        }
        const ParametersArr = decodeURIComponent(ParametersStr || "").split("|");
        for (let i = 0; i < ParametersArr.length; i++) {
            let field = "";
            let value = "";
            const s = ParametersArr[i].split("~");
            for (let j = 0; j < s.length; j++) {
                const type = s[j].substring(0, 2);
                if (type === "F:") {
                    field = s[j].substring(2);
                } else if (type === "V:") {
                    value = s[j].substring(2);
                }
            }
            if (field) {
                parameter[field] = value;
            }
        }
        return parameter;
    }

    static ParametersToUrl(Parameters, encodeURI = true) {
        const url = [];
        const keys = Object.keys(Parameters);
        const len = keys.length;
        for (let i = 0; i < len; i++) {
            // Task 640220: Pinned filter in Fusion report cuts off value, causes report to return no data
            if (encodeURI) {
                url.push(`F:${encodeURIComponent(keys[i])}~V:${encodeURIComponent(Parameters[keys[i]])}`);
            } else {
                // Task // Task 711434: Accounting Reports - Request for Input about Best Practices
                url.push(`F:${keys[i]}~V:${Parameters[keys[i]]}`);
            }
        }
        return url.join("|");
    }

    // EC-6142: Add support for Fusion Comments for reports on dashboards
    // ---------------Comments--------------------------
    // Add Comments
    static async CommentHttpRequest(Action, JsonBody) {
        const myRequest = new ECP.EC_Request("Add_ReportComments");
        myRequest.SetReturnType(ECP.EC_ReturnType.Json);
        myRequest.AddRequestVariable("Action", Action);
        myRequest.AddRequestVariable("JsonBody", JsonBody, ECP.EC_Verb.Post);
        const data = await myRequest.Submit();
        return data;
    }

    static SetCommentCount() {
        const Count = document.querySelectorAll("#CommentsHtml .item-box.comment-item").length;
        if (Count === 0) {
            document.querySelector(".comments-count").innerHTML = "";
            document.querySelector(".empty-message").style.display = "";
        } else {
            document.querySelector(".empty-message").style.display = "none";
            document.querySelector(".comments-count").innerHTML = Count;
        }
    }

    async CommentsAction(Action, args) {
        if (!args) {
            args = {};
        }
        let currentItemBox = args.currentMessageItem ? args.currentMessageItem.parentNode : null;
        let currentCellPosition = currentItemBox ? currentItemBox.getAttribute("cellposition") : null;
        if (currentItemBox && currentItemBox.id.startsWith("AddCommentMenu")) {
            currentCellPosition = currentItemBox.querySelector(".comment-item").getAttribute("cellposition");
        }
        const currentCommentID = args.currentMessageItem && args.currentMessageItem.querySelector(".comment-actions") ? args.currentMessageItem.querySelector(".comment-actions").getAttribute("commentid") : null;
        const itemAuthID = args.currentMessageItem && args.currentMessageItem.querySelector(".message-detail .user-name") ? args.currentMessageItem.querySelector(".message-detail .user-name").getAttribute("authenticationid") : null;
        const action = Action;
        try {
            switch (action) {
                case "View":
                    return await FluxDisplay.CommentHttpRequest(action, `{"ReportID":"${this.ReportID}"}`);
                case "Add":
                    if (this.IsFusionView) {
                        document.querySelector(".empty-message").style.display = "none";
                        document.getElementById("CommentsIcon").setAttribute("selected", "selected");
                        const addCommentBox = await this.AddCommentReplyBox({ type: "New", cellposition: args.commentFlag });
                        const fusionMenuMainItem = document.querySelector("#CommentsHtml .fusion-menu-main-item");
                        fusionMenuMainItem.appendChild(addCommentBox);
                        fusionMenuMainItem.querySelector(".reply-item").scrollIntoView({
                            behavior: "auto",
                            block: "end"
                        });
                        await ToggleQuickcontent("Comments");
                    } else {
                        const addCommentBox = await this.AddCommentReplyBox({ type: "New", cellposition: args.commentFlag });
                        const menu = document.createElement("ecp-menu");
                        menu.setAttribute("id", `AddCommentMenu_${EC_Fmt.MD5(args.commentFlag)}`);
                        const commentHeader = document.createElement("div");
                        commentHeader.className = "CommentHeader";
                        commentHeader.innerHTML = `
                            <span>Comments</span>
                            <ecp-badge value="0" id="CommentCount"></ecp-badge>
                        `;
                        menu.appendChild(commentHeader);
                        menu.appendChild(addCommentBox);
                        document.body.appendChild(menu);
                        menu.ShowMenu(args.cellDiv);
                    }
                    break;
                case "Save":
                    ECP.Dialog.ShowLoading("Saving...", currentItemBox);
                    const currentComment = args.currentMessageItem.querySelector("ecp-input").value;
                    if (EC_Fmt.isNull(currentComment)) {
                        ECP.Dialog.Alert("Comment content cannot be empty");
                    } else {
                        const jsonBodyData = {
                            ReportID: this.ReportID.toString(),
                            Comments: currentComment.toString(),
                            CellPosition: currentCellPosition.toString(),
                            ReportCommentID: (currentCommentID || "").toString()
                        };
                        const data = await FluxDisplay.CommentHttpRequest(action, JSON.stringify(jsonBodyData));

                        if (!currentCommentID) {
                            const newReplyBox = await this.AddCommentReplyBox({
                                type: "Reply",
                                cellposition: currentCellPosition
                            });

                            if (this.IsFusionView) {
                                currentItemBox.appendChild(newReplyBox);
                                currentItemBox.querySelector(`ecp-btn[cellposition="${currentCellPosition}"]`).style.display = "";
                                FluxDisplay.SetCommentCount();
                                currentItemBox.querySelector(".reply-item").scrollIntoView({
                                    behavior: "auto",
                                    block: "end"
                                });
                            } else if (currentItemBox.id.startsWith("AddCommentMenu")) {
                                args.currentMessageItem = currentItemBox.querySelector(".reply-item");
                                currentItemBox.querySelector(".comment-item").appendChild(newReplyBox);
                                currentItemBox.querySelector("#CommentCount").value = parseInt(currentItemBox.querySelector("#CommentCount").value) + 1;
                            } else {
                                currentItemBox.appendChild(newReplyBox);
                                currentItemBox.parentNode.querySelector("#CommentCount").value = parseInt(currentItemBox.parentNode.querySelector("#CommentCount").value) + 1;
                                currentItemBox = currentItemBox.parentNode;
                            }
                        }

                        this.SetCommentEditItem({
                            type: "Remove",
                            currentEditItem: args.currentMessageItem,
                            currentComment,
                            commentID: data.reportCommentID
                        });

                        this.AddCellDotIcon({
                            type: "Add",
                            cellposition: currentCellPosition
                        });
                        if (!this.IsFusionView && !EC_Fmt.isMobile) {
                            currentItemBox.CloseMenu(document.querySelector(`td[cellposition="${currentCellPosition}"]`));
                            currentItemBox.ShowMenu(document.querySelector(`td[cellposition="${currentCellPosition}"]`));
                        }
                    }
                    break;
                case "Edit":
                    ECP.Dialog.ShowLoading("Editing...", currentItemBox);
                    if (AuthenticationID.toString() !== itemAuthID) {
                        ECP.HTML.Snackbar("You are not the creator of this comment and cannot edit it.", "Error");
                        ECP.Dialog.HideLoading();
                        break;
                    }
                    const EditCommentText = args.currentMessageItem.querySelector(".message-text-box").innerHTML;
                    this.RemoveActiveComment(currentCellPosition);

                    if (!currentItemBox.classList.contains("active")) {
                        currentItemBox.classList.add("active");
                    }
                    this.SetCommentEditItem({
                        type: "Add",
                        currentEditItem: args.currentMessageItem,
                        currentComment: EditCommentText,
                        commentID: currentCommentID
                    });
                    this.AddCellDotIcon({
                        type: "Add",
                        cellposition: currentCellPosition
                    });

                    currentItemBox.querySelector(".reply-item").scrollIntoView({
                        behavior: "auto",
                        block: "end"
                    });
                    break;
                case "Delete":
                    ECP.Dialog.ShowLoading(`Deleting entire comment...`, currentItemBox);
                    const ReportOwnerAuthenticationID = document.getElementById("report-OwnerAuthenticationID").value;
                    if (AuthenticationID !== parseInt(ReportOwnerAuthenticationID)) {
                        ECP.HTML.Snackbar("You are not the creator of this report, so you cannot delete it.", "Error");
                        ECP.Dialog.HideLoading();
                        break;
                    }
                    const jsonBodyData = {
                        ReportID: this.ReportID.toString(),
                        CellPosition: currentCellPosition.toString()
                    };

                    await FluxDisplay.CommentHttpRequest(action, JSON.stringify(jsonBodyData));

                    this.AddCellDotIcon({
                        type: "Load",
                        deleteIcon: true,
                        cellposition: currentCellPosition
                    });

                    currentItemBox.remove();
                    FluxDisplay.SetCommentCount();
                    break;
                case "DeleteSingle":
                    ECP.Dialog.ShowLoading(`Deleting single comment...`, currentItemBox);
                    if (AuthenticationID.toString() !== itemAuthID) {
                        ECP.HTML.Snackbar("You are not the creator of this comment and cannot delete it.", "Error");
                        ECP.Dialog.HideLoading();
                        break;
                    }
                    const messageItemArr = currentItemBox.querySelectorAll(".message-item");
                    const jsonBody = {
                        ReportID: this.ReportID.toString(),
                        ReportCommentID: currentCommentID.toString()
                    };
                    await FluxDisplay.CommentHttpRequest(action, JSON.stringify(jsonBody));
                    this.RemoveActiveComment(currentCellPosition);
                    // if (!currentItemBox.classList.contains("active")) {
                    //     currentItemBox.classList.add("active");
                    // }
                    if (messageItemArr.length === 1 || (messageItemArr.length === 2 && currentItemBox.querySelector(".reply-item"))) {
                        this.AddCellDotIcon({
                            type: "Load",
                            deleteIcon: true,
                            cellposition: currentCellPosition
                        });
                        // args.currentMessageItem.remove();
                        currentItemBox.parentNode.removeChild(currentItemBox);
                        FluxDisplay.SetCommentCount();
                    } else {
                        this.AddCellDotIcon({
                            type: "Add",
                            cellposition: currentCellPosition
                        });
                        // args.currentMessageItem.remove();
                        currentItemBox.removeChild(args.currentMessageItem);
                    }
                    break;
            }
        } catch (error) {
            ECP.Dialog.Alert(`An error occurred in CommentsAction: ${error}`);
        } finally {
            ECP.Dialog.HideLoading();
        }
    }

    async LoadCommentHtml(IsFusionView) {
        this.IsFusionView = IsFusionView;
        let CommentsHtml = "";
        if (this.IsFusionView) {
            CommentsHtml = document.getElementById("CommentsHtml");
            if (!EC_Fmt.isNull(CommentsHtml.innerHTML)) {
                const cellPositionList = CommentsHtml.querySelectorAll(".item-box");
                for (let i = 0; i < cellPositionList.length; i++) {
                    this.AddCellDotIcon({
                        type: "Load",
                        cellposition: cellPositionList[i].getAttribute("cellposition")
                    });
                }
                return;
            }
            CommentsHtml.innerHTML = `<div class="fusion-menu-main-item">
                <div class="item-header">
                    <span class="header-strong">Comments</span>
                    <span class="header-secondary">See a list of all</span>
                </div>
                <div class="empty-message">
                    <ecp-empty illustration="empty">
                        <ecp-empty-title>No Comments Yet</ecp-empty-title>
                        <ecp-empty-body>To add a comment, click on the cell you want to comment on and select the 'Add
                            Comments' option at the top of the drop down</ecp-empty-body>
                    </ecp-empty>
                </div>
            </div>`;
        }
        const respData = await this.CommentsAction("View");
        let template = "";
        if (respData.length > 0) {
            respData.sort((a, b) => {
                const positionComparison = a.cellPosition.localeCompare(b.cellPosition);
                if (positionComparison !== 0) return positionComparison;
                const dateComparison = new Date(a.commentDate).getTime() - new Date(b.commentDate).getTime();
                if (dateComparison !== 0) return dateComparison;
                return a.reportCommentID - b.reportCommentID;
            });
            const groupedComments = respData.reduce((acc, comment) => {
                if (!acc[comment.cellPosition]) {
                    acc[comment.cellPosition] = [];
                }
                acc[comment.cellPosition].push(comment);
                return acc;
            }, {});
            Object.entries(groupedComments).forEach(([cellPosition, comments]) => {
                if (this.IsFusionView) {
                    template += `
                    <div class="item-box comment-item" cellposition="${cellPosition}">
                        <ecp-btn
                            id="DeleteCommentBtn_${cellPosition}"
                            type="ghost"
                            icon="ews-icon-delete"
                            cellposition="${cellPosition}"
                            ${AuthenticationID === parseInt(document.getElementById("report-OwnerAuthenticationID").value) ? "" : "disabled"}
                        ></ecp-btn>
                        <ecp-tooltip for="DeleteCommentBtn_${cellPosition}" placement="Bottom" delayms="100">
                            Delete this section of comments.
                        </ecp-tooltip>
                `;
                } else {
                    template += `
                    <ecp-menu id="AddCommentMenu_${EC_Fmt.MD5(cellPosition)}">
                        <div class="CommentHeader">
                            <span>Comments</span>
                            <ecp-badge value="${comments.length}" id="CommentCount"></ecp-badge>
                        </div>
                        <div class="item-box comment-item" cellposition="${cellPosition}">
                `;
                }
                comments.forEach((comment) => {
                    const IsSameAuthenticationID = comment.ownerAuthenticationID === AuthenticationID.toString();
                    template += `
                    <div class="message-item">
                        <img class="user-picture" src="${comment.pictureURL}">
                        <div class="message-detail">
                            <div>
                                <span class="user-name" authenticationid="${comment.ownerAuthenticationID}">
                                    ${comment.userName}
                                </span>
                                <span class="datetime">${comment.commentDate}</span>
                            </div>
                            <div class="message-text-box">${comment.comments}</div>
                            ${IsSameAuthenticationID && this.IsFusionView ? `
                                <div class="comment-actions ${!EC_Fmt.isMobile ? "Hover-display" : ""}" commentid="${comment.reportCommentID}">
                                    <span class="ews-icon-delete delete-btn" commentid="${comment.reportCommentID}"></span>
                                    <span class="ews-icon-edit edit-btn" commentid="${comment.reportCommentID}"></span>
                                </div>
                            ` : ""}
                        </div>
                    </div>
                `;
                });

                if (this.IsFusionView) {
                    template += "</div>";
                } else {
                    template += "</div></ecp-menu>";
                    CommentsHtml = EC_Fmt.HtmlStrToElement(template);
                    document.body.appendChild(CommentsHtml);
                    template = "";
                }

                this.AddCellDotIcon({
                    type: "Load",
                    cellposition: cellPosition
                });
            });
        }

        if (this.IsFusionView) {
            CommentsHtml.querySelector(".fusion-menu-main-item .item-header").parentNode.insertAdjacentHTML("beforeend", template);
            FluxDisplay.SetCommentCount();
            CommentsHtml.addEventListener("click", (e) => {
                this.CommenClickEve(e);
            });
        }
    }

    CommenClickEve(e) {
        const CurInput = e.target;
        if (CurInput) {
            e.stopPropagation();
            const commentItem = CurInput.closest(".comment-item");
            if ((commentItem || CurInput.classList.contains("message-item")) && !CurInput.classList.contains("edit-btn") && !CurInput.classList.contains("delete-btn")) {
                this.CommentItemActiveToggle(commentItem || CurInput);
            }
            const messageItem = CurInput.closest(".message-item");
            if (CurInput.id === "saveBtn") {
                this.CommentsAction("Save", { currentMessageItem: messageItem });
            } else if (CurInput.classList.contains("edit-btn")) {
                this.CommentsAction("Edit", { currentMessageItem: messageItem });
            } else if (CurInput.classList.contains("delete-btn")) {
                this.CommentsAction("DeleteSingle", { currentMessageItem: messageItem });
            } else if (CurInput.id && CurInput.id.startsWith("DeleteCommentBtn")) {
                this.CommentsAction("Delete", { currentMessageItem: CurInput });
            }
        }
    }

    AddCellDotIcon(args) {
        requestAnimationFrame(() => {
            const fusionTableDivArr = document.querySelectorAll(".FusionTable");
            for (let i = 0; i < fusionTableDivArr.length; i++) {
                const fusionTableDiv = fusionTableDivArr[i];
                if (!fusionTableDiv) {
                    continue;
                }
                const commentFlagArr = args.cellposition.split("|");
                let commentFlag = `tr[parent='${commentFlagArr[0]}']`;
                const queryParentNode = fusionTableDiv.querySelector(commentFlag);
                if (!queryParentNode) {
                    continue;
                }
                let IsCollapse = queryParentNode.querySelector(".ews-icon-insertsheet");
                if (args.type === "Add" && IsCollapse && !IsCollapse.classList.contains("rotateicon")) {
                    IsCollapse.click();
                }

                if (commentFlagArr.some(item => item.startsWith("Child"))) {
                    commentFlag += `[child="${commentFlagArr[1]}"]`;
                    const queryChildNode = fusionTableDiv.querySelector(commentFlag);
                    if (!queryChildNode) {
                        continue;
                    }
                    IsCollapse = queryChildNode.querySelector(".ews-icon-insertsheet");
                    if (args.type === "Add" && IsCollapse && !IsCollapse.classList.contains("rotateicon")) {
                        IsCollapse.click();
                    }
                }

                if (commentFlagArr.some(item => item.startsWith("Detail"))) {
                    commentFlag += `[detail="${commentFlagArr[2]}"]`;
                }
                commentFlag += ` td span[field="${commentFlagArr[commentFlagArr.length - 1]}"]`;
                let cellDiv = fusionTableDiv.querySelector(commentFlag);
                if (cellDiv) {
                    cellDiv = cellDiv.parentNode;
                } else {
                    continue;
                }
                const insertSpan = EC_Fmt.HtmlStrToElement(`<span class=ews-icon-circleclosed id=selectedcell></span>`);

                if (!cellDiv.hasAttribute("cellposition")) {
                    cellDiv.setAttribute("cellposition", args.cellposition);
                    if (!cellDiv.hasAttribute("right")) {
                        cellDiv.appendChild(insertSpan);
                    } else {
                        cellDiv.insertBefore(insertSpan, cellDiv.querySelector("span"));
                    }
                } else {
                    const existSelectedCell = cellDiv.querySelector(`span[id="selectedcell"]`);
                    if (!existSelectedCell) {
                        if (!cellDiv.hasAttribute("right")) {
                            cellDiv.appendChild(insertSpan);
                        } else {
                            cellDiv.insertBefore(insertSpan, cellDiv.querySelector("span"));
                        }
                    }
                    if (args.deleteIcon) {
                        cellDiv.removeAttribute("cellposition");
                        cellDiv.removeChild(existSelectedCell);
                    }
                }

                if (args.type === "Add") {
                    cellDiv.setAttribute("active", "");
                    if (this.IsFusionView) {
                        cellDiv.scrollIntoView({
                            behavior: "auto",
                            block: "center"
                        });
                    }
                } else {
                    cellDiv.removeAttribute("active");
                }
                insertSpan.addEventListener("click", (e) => { this.Expand_Current_Comment(e); });
            }
        });
    }

    async Expand_Current_Comment(event) {
        const me = event.target;
        event.stopPropagation();
        if (EC_Fmt.isMobile && this.IsFusionView) {
            document.querySelector(".PhoneQuickEditButton").click();
        }
        if (this.IsFusionView) {
            document.getElementById("CommentsIcon").setAttribute("selected", "selected");
            await ToggleQuickcontent("Comments");
        }

        const cellpositionValue = me.parentNode.getAttribute("cellposition");
        await this.CommentItemActiveToggle(document.querySelector(`.comment-item[cellposition="${cellpositionValue}"]`));
    }

    RemoveActiveComment(args) {
        const activeCell = document.querySelectorAll(".FusionTable td[cellposition][active]");
        for (let i = 0; i < activeCell.length; i++) {
            if (args !== activeCell[i].getAttribute("cellposition")) {
                activeCell[i].removeAttribute("active");
                if (!activeCell[i].querySelector("#selectedcell")) {
                    activeCell[i].removeAttribute("cellposition");
                }
            }
        }

        const activeComment = document.querySelectorAll(".comment-item.active");
        for (let k = 0; k < activeComment.length; k++) {
            if (args !== activeComment[k].getAttribute("cellposition")) {
                const messageItemArr = activeComment[k].querySelectorAll(".message-item");
                for (let i = 0; i < messageItemArr.length; i++) {
                    if (messageItemArr[i].classList.contains("edit")) {
                        this.SetCommentEditItem({
                            type: "Remove",
                            currentEditItem: messageItemArr[i],
                            currentComment: messageItemArr[i].querySelector("ecp-input").value,
                            commentID: messageItemArr[i].querySelector("#saveBtn").getAttribute("commentid")
                        });
                    }
                }

                activeComment[k].classList.remove("active");
                const replyItemExist = activeComment[k].querySelector(".reply-item");
                if (replyItemExist) {
                    replyItemExist.remove();
                    if (messageItemArr.length === 1) {
                        activeComment[k].parentNode.removeChild(activeComment[k]);
                    }
                }
            }
        }
    }

    SetCommentEditItem(args) {
        let newCommentActionsHtml = "";
        let editItem = `<div class="message-text-box">${args.currentComment}</div>`;
        if (this.IsFusionView) {
            newCommentActionsHtml = `<div class="comment-actions ${!EC_Fmt.isMobile ? "Hover-display" : ""}" commentid="${args.commentID}">
            <span class="ews-icon-delete delete-btn" commentid="${args.commentID}"></span>
            <span class="ews-icon-edit edit-btn" commentid="${args.commentID}"></span>
            </div>`;
        }
        if (args.type === "Remove") {
            args.currentEditItem.classList.remove("reply-item");
            args.currentEditItem.classList.remove("edit");
            args.currentEditItem.querySelector(".reply-box").outerHTML = editItem;
        } else {
            editItem = `<div class=reply-box> <ecp-input type=textarea value="${args.currentComment}"></ecp-input> </div>`;
            newCommentActionsHtml = `<div class=comment-actions commentid="${args.commentID}"> <ecp-btn type=primary id=saveBtn commentid="${args.commentID}">Save</ecp-btn> </div>`;
            args.currentEditItem.classList.add("reply-item");
            args.currentEditItem.classList.add("edit");
            args.currentEditItem.querySelector(".message-text-box").outerHTML = editItem;
        }
        args.currentEditItem.querySelector(".comment-actions").outerHTML = newCommentActionsHtml;
    }

    async AddCommentReplyBox(args) {
        let newItem;
        const dateTimeNow = EC_Fmt.DateTimeParseFormat(EC_Fmt.ScreenFmt(new Date(), ECP.DataType._DateTime));
        const formattedWithoutSeconds = `${dateTimeNow.split(":").slice(0, -1).join(":")} ${dateTimeNow.split(" ")[2]}`;
        let pictureURL = "https://cdn.e8.co/GlobalDocs/587681.png";
        let userName = "None";
        const userImgClass = document.querySelector(".UserStyle img");
        const userNameClass = document.querySelector(".UserStyle .lap-header-user");
        if (userImgClass) {
            pictureURL = userImgClass.getAttribute("src");
        }
        if (userNameClass) {
            userName = userNameClass.childNodes[0].data.trim();
        }

        const replyItem = document.createElement("div");
        replyItem.classList.add("message-item");
        replyItem.classList.add("reply-item");
        const replyHtml = `<img class=user-picture src="${pictureURL}" alt="">`
            + "<div class=message-detail>"
            + `<div><span class=user-name authenticationid="${AuthenticationID}">${userName}</span><span class=datetime>${formattedWithoutSeconds}</span></div>`
            + `<div class=reply-box><ecp-input type=textarea placeholder="Reply to comment"></ecp-input></div>`
            + `<div class=comment-actions><ecp-btn type=primary id=saveBtn>Save</ecp-btn></div>`
            + `</div>`;

        replyItem.innerHTML = replyHtml;

        if (args.type === "New") {
            newItem = document.createElement("div");
            newItem.classList.add("item-box");
            newItem.classList.add("comment-item");
            newItem.classList.add("active");
            newItem.setAttribute("cellposition", args.cellposition);
            if (this.IsFusionView) {
                let deleteBtn = `<ecp-btn id=DeleteCommentBtn_New type=ghost icon=ews-icon-delete cellposition="${args.cellposition}" style="display:none;"></ecp-btn>`
                    + `<ecp-tooltip for=DeleteCommentBtn_New placement=Bottom delayms=100>Delete this section of comments.</ecp-tooltip>`;
                const ReportOwnerAuthenticationID = document.getElementById("report-OwnerAuthenticationID").value;
                if (AuthenticationID !== parseInt(ReportOwnerAuthenticationID)) {
                    deleteBtn = `<ecp-btn id=DeleteCommentBtn_New type=ghost icon=ews-icon-delete cellposition="${args.cellposition}" style="display:none;" disabled></ecp-btn>`
                        + `<ecp-tooltip for=DeleteCommentBtn_New placement=Bottom delayms=100>Must be the report owner</ecp-tooltip>`;
                }
                newItem.innerHTML = deleteBtn;
            }

            newItem.appendChild(replyItem);
        } else {
            newItem = replyItem;
        }

        if (!this.IsFusionView) {
            newItem.querySelector("#saveBtn").addEventListener("click", () => {
                this.CommentsAction("Save", { currentMessageItem: newItem });
            });
        }

        return newItem;
    }

    async CommentItemActiveToggle(args) {
        if (args && !args.classList.contains("active")) {
            const currentCellPosition = args.getAttribute("cellposition");
            this.RemoveActiveComment(currentCellPosition);
            args.classList.add("active");
            const replyItemBox = await this.AddCommentReplyBox({
                type: "Reply",
                cellposition: currentCellPosition
            });

            args.appendChild(replyItemBox);
            if (!this.IsFusionView) {
                const targetCell = document.querySelector(`td[cellposition="${currentCellPosition}"]`);
                args.parentNode.ShowMenu(targetCell);
            }

            setTimeout(() => {
                args.querySelector(".reply-item").scrollIntoView({
                    behavior: "auto",
                    block: "end"
                });
            }, 0);

            this.AddCellDotIcon({
                type: "Add",
                cellposition: currentCellPosition
            });
        } else if (args && !this.IsFusionView) {
            const targetCell = document.querySelector(`td[cellposition="${args.getAttribute("cellposition")}"]`);
            args.parentNode.ShowMenu(targetCell);
        }
    }
}

// eslint-disable-next-line no-unused-vars
function FillFusionReportToElements(el, ReportID) {
    return new Promise((resolve, reject) => {
        EC_FluxView.FillToElements({ el, ReportID }).then(() => {
            const myFluxDisplay = new FluxDisplay({ el, ReportID });
            myFluxDisplay.Init();
        }).catch(reject);
    });
}

function mergeCustomFilter(customFilterStr) {
    const params = {};

    const regex = /F:([^~]+)~V:([^~]+)~O:([^|]+)/g;
    const matches = [...customFilterStr.matchAll(regex)];
    matches.forEach((match) => {
        const [_, key, valueStr, operator] = match;
        const value = valueStr.replaceAll("^", "%5E").split("%5E").map(decodeURIComponent);
        const paramKey = `${key}_${operator}`;
        params[paramKey] = params[paramKey] || new Set();

        value.forEach(val => params[paramKey].add(val));
    });

    const mergedParams = Object.entries(params)
        .filter(([_, values]) => values.size > 0)
        .map(([paramKey, values]) => {
            const mergedValue = Array.from(values).join("%5E");
            const [key, operator] = paramKey.split("_");
            return `F:${key}~V:${mergedValue}~O:${operator}`;
        });

    return mergedParams.join("|");
}
